// AdminRetailerDashboard.js - Final Technical Manifest Version
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import html2canvas from 'html2canvas'; // kept for non-blank-canvas templates
import { prepareScene, renderToImage, renderToHTML, RENDER_BACKENDS } from '../utils/RenderEngine';
import { buildDealSignalSchema } from '../utils/CreativeSchema';
import {
  BRAND_STORY_SLIDES,
  BRAND_STORY_SLIDE_COUNT,
  BRAND_STORY_CAPTION,
  buildBrandStorySlideHTML,
  buildAllBrandStorySlideHTMLs,
} from '../utils/brandStoryCarousel';
import { parseGIF, decompressFrames } from 'gifuct-js';
import JSZip from 'jszip';
import Pagination from '../JSComponents/Pagination';
import {
  FaChartLine, FaLink, FaDatabase, FaImage, FaSignOutAlt, FaTrash, FaPlus, FaSyncAlt, FaTerminal, FaCode, FaExchangeAlt, FaShieldAlt,
  FaSatellite, FaMicrochip, FaNetworkWired, FaWifi, FaBolt, FaLayerGroup, FaSlidersH, FaBullseye, FaMagic, FaHandshake, FaMousePointer,
  FaAd, FaMobileAlt, FaDesktop, FaDownload, FaArrowUp, FaArrowDown, FaFilter, FaSort, FaChevronDown, FaCheck, FaTimes, FaChevronLeft, FaChevronRight, FaCopy
} from 'react-icons/fa';
import '../ProfileStyles/profile-customer.css';

function AdminRetailerDashboard({ currentUser, onLogout }) {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([
    { id: 'blank_canvas', name: 'Blank Canvas', description: 'Deal Signal — Meta feed ad with live savings + optional product hero' },
    { id: 'brand_story', name: 'Brand Story', description: '5-slide Instagram carousel — who Stockroom is' },
    { id: 'product_showcase', name: 'Product Showcase', description: 'Premium spotlight on a single item' },
    { id: 'noir_duality', name: 'Clothing Showcase', description: 'Dark split-screen design' },
    { id: 'single_product_shoes', name: 'Single Product (Shoes)', description: 'Full-bleed shoe asset' },
    { id: 'single_product_clothing', name: 'Single Product (Clothing)', description: 'Full-bleed clothing asset' },
    { id: 'ad_creative', name: 'Ad Creative', description: 'Data-driven overview' },
    { id: 'official_partner', name: 'Official Partner', description: 'Branded background asset' }
  ]);

  const moveTemplate = (index, direction) => {
    const newTemplates = [...templates];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= templates.length) return;
    
    // Swap elements
    [newTemplates[index], newTemplates[targetIndex]] = [newTemplates[targetIndex], newTemplates[index]];
    setTemplates(newTemplates);
  };

  const generateDuoNodes = (selectedAdProducts) => {
    if (selectedAdProducts.length === 0) return `<div style="grid-column: 1/-1; padding: 100px; text-align: center; border: 1px dashed #222; color: #444;">AWAITING_ASSET_SELECTION...</div>`;
    const limit = adViewMode === 'mobile' ? 4 : 2;
    return selectedAdProducts.slice(0, limit).map(p => {
      const hasDiscount = p.originalPrice && p.originalPrice > p.price;
      const discount = p.discountPercentage || (hasDiscount ? Math.round((1 - p.price / p.originalPrice) * 100) : 0);

      return `
        <div class="duo-panel">
          <div class="duo-img-cont" style="background-image: url(${p.image || p.images?.[0]})">
            <div class="duo-overlay-meta">
              <div class="duo-brand">${p.brand ? p.brand.toUpperCase() : 'STOCKROOM'}</div>
              <div class="duo-name">${p.name}</div>
              <div class="duo-prices">
                <div class="duo-price-wrap">
                  ${hasDiscount ? `<span class="duo-was">$${p.originalPrice}</span>` : ''}
                  <span class="duo-now">$${p.price}</span>
                </div>
                ${discount > 0 ? `<span class="duo-pct">-${discount}%</span>` : ''}
              </div>
            </div>
          </div>
        </div>
      `;
    }).join('');
  };

  const [selectedTab, setSelectedTab] = useState('retailer-hub');
  const [bannerSection, setBannerSection] = useState('hero'); // 'hero' or 'category'
  const [promotions, setPromotions] = useState([]);
  const [reports, setReports] = useState(null);
  const [adminReport, setAdminReport] = useState(null); // Click data
  // Analytics tab state
  const [analyticsRange, setAnalyticsRange] = useState('30d');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsSubTab, setAnalyticsSubTab] = useState('overview'); // 'overview' | 'products' | 'sections' | 'brands'
  const [apiKeys, setApiKeys] = useState([]);
  const [activeBrands, setActiveBrands] = useState([]);
  const [allRetailers, setAllRetailers] = useState([]);
  const [siteRetailers, setSiteRetailers] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [syncLogs, setSyncLogs] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [discoveredMerchants, setDiscoveredMerchants] = useState([]);
  const [collisions, setCollisions] = useState([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  
  // Retailer Hub + Ingestion Engine sub-tab state
  const [retailerHubSubTab, setRetailerHubSubTab] = useState('overview'); // 'overview' | 'integrations' | 'affiliate' | 'activity'
  const [ingestionSubTab, setIngestionSubTab] = useState('pipeline'); // 'pipeline' | 'normalizer' | 'orchestrator'

  // Advertising State
  const [adSubTab, setAdSubTab] = useState('blueprint'); // 'blueprint', 'config', 'manifest'
  const [adViewMode, setAdViewMode] = useState('mobile'); // 'desktop', 'mobile', or 'square'
  const [adProducts, setAdProducts] = useState([]);
  const [adSearchTerm, setAdSearchTerm] = useState('');
  const [selectedAdProducts, setSelectedAdProducts] = useState([]);
  const [adStyleCode, setAdStyleCode] = useState('');
  const [showAdOverlay, setShowAdOverlay] = useState(false);
  const [adScale, setAdScale] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('noir_duality');
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [partnerCustomText, setPartnerCustomText] = useState('');
  const [partnerFontSize, setPartnerFontSize] = useState(12); // Default 12vw or proportional size
  const [autoGeneratedAds, setAutoGeneratedAds] = useState([]);
  const [curationPool, setCurationPool] = useState([]);
  
  // New Advertising Advanced State
  const [adSortConfig, setAdSortConfig] = useState(null);
  const [adFilters, setAdFilters] = useState({ brand: [], retailer: [], category: [], gender: [] });
  const [showFilterDropdown, setShowFilterDropdown] = useState(null);
  const [adColorTheme, setAdColorTheme] = useState('noir');
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [adPage, setAdPage] = useState(1);
  const [adTotalPages, setAdTotalPages] = useState(1);
  const [savingsStats, setSavingsStats] = useState({ totalSavings: 0, averageSavingsPercentage: 0, totalDeals: 0, activeUsers: 1 });
  const [adPosition, setAdPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [brandStorySlideIndex, setBrandStorySlideIndex] = useState(0);
  const [captionCopied, setCaptionCopied] = useState(false);

  const handleMouseDown = (e) => {
    if (selectedTemplate !== 'single_product_shoes' && selectedTemplate !== 'single_product_clothing') return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - adPosition.x,
      y: e.clientY - adPosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setAdPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => setIsDragging(false);


  const COLOR_THEMES = {
    noir: { name: 'NOIR_PROTOCOL', linkBio: '#00E676', background: '#000000', signature: '#ffffff', sigOpacity: 0.15 },
    albino: { name: 'ALBINO_MANIFEST', linkBio: '#00E676', background: '#ffffff', signature: '#000000', sigOpacity: 0.1 },
    radioactive: { name: 'RADIOACTIVE_SIGNAL', linkBio: '#ffffff', background: '#000000', signature: '#00E676', sigOpacity: 0.2 },
    high_vis: { name: 'HIGH_VIS_CORE', linkBio: '#000000', background: '#ffffff', signature: '#00E676', sigOpacity: 0.15 }
  };


  const filteredAndSortedProducts = useMemo(() => {
    let result = [...adProducts].map(p => {
      const hasDiscount = p.originalPrice && p.originalPrice > p.price;
      const discount = p.discountPercentage || (hasDiscount ? Math.round((1 - p.price / p.originalPrice) * 100) : 0);
      return { ...p, calculatedDiscount: discount };
    });

    if (adFilters.brand.length > 0) {
      result = result.filter(p => adFilters.brand && adFilters.brand.includes(p.brand));
    }
    if (adFilters.retailer.length > 0) {
      result = result.filter(p => adFilters.retailer && adFilters.retailer.includes(p.merchantName));
    }
    if (adFilters.category && adFilters.category.length > 0) {
      result = result.filter(p => adFilters.category.includes(p.category));
    }
    if (adFilters.gender && adFilters.gender.length > 0) {
      result = result.filter(p => adFilters.gender.includes(p.gender));
    }

    if (adSortConfig) {
      result.sort((a, b) => {
        let aVal = a[adSortConfig.key];
        let bVal = b[adSortConfig.key];
        if (aVal < bVal) return adSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return adSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [adProducts, adSortConfig, adFilters]);

  const toggleSort = (key) => {
    setAdSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'desc' };
      if (prev.direction === 'desc') return { key, direction: 'asc' };
      return null;
    });
  };

  const toggleFilter = (type, value) => {
    setAdFilters(prev => {
      const current = prev[type];
      const next = current.includes(value) 
        ? current.filter(v => v !== value) 
        : [...current, value];
      return { ...prev, [type]: next };
    });
    setAdPage(1); // Reset to page 1 on filter change
  };

  const uniqueFilterOptions = useMemo(() => {
    const brands = [...new Set([...activeBrands.map(b => b.name), ...adProducts.map(p => p.brand)])].filter(Boolean).sort();
    const retailers = [...new Set([...allRetailers.map(r => r.name), ...adProducts.map(p => p.merchantName)])].filter(Boolean).sort();
    const categories = ['T-Shirts', 'Hoodies', 'Pants', 'Shorts', 'Jeans', 'Sweaters', 'Jackets', 'Blazers', 'Dresses', 'Skirts', 'Outerwear', 'Activewear', 'Swimwear', 'Shoes', 'Accessories'].sort();
    const genders = ['women', 'men', 'unisex', 'kids'];
    return { brands, retailers, categories, genders };
  }, [adProducts, activeBrands, allRetailers]);

  
  // New Partner Assets State
  const [partnerDesktopBanner, setPartnerDesktopBanner] = useState(null);
  const [partnerMobileBanner, setPartnerMobileBanner] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [gifFrames, setGifFrames] = useState([]);
  const iframeRef = useRef(null);
  const previewRef = useRef(null);

  const generateAutoAds = () => {
    if (curationPool.length < 4) return;
    const newAds = [];
    for (let i = 0; i < 6; i++) {
      const shuffled = [...curationPool].sort(() => 0.5 - Math.random());
      newAds.push({
        id: Math.random().toString(36).substr(2, 9),
        products: shuffled.slice(0, 4),
        category: shuffled[0].categories?.[0] || 'Mixed'
      });
    }
    setAutoGeneratedAds(newAds);
  };

  const handleDownloadBrandStoryCarousel = async () => {
    setIsDownloading(true);
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Brand Story Export');
    iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:1080px;height:1080px;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);

    try {
      const zip = new JSZip();
      const slides = buildAllBrandStorySlideHTMLs();

      for (let i = 0; i < slides.length; i += 1) {
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('SLIDE_LOAD_TIMEOUT')), 12000);
          iframe.onload = () => {
            clearTimeout(timer);
            resolve();
          };
          iframe.onerror = () => {
            clearTimeout(timer);
            reject(new Error('SLIDE_LOAD_FAILED'));
          };
          iframe.srcdoc = slides[i];
        });

        // Allow CDN fonts a beat to settle before freeze/capture
        await new Promise((r) => setTimeout(r, 600));

        const cleanup = await prepareScene(iframe, { width: 1080, height: 1080 });
        try {
          const blob = await renderToImage(iframe, {
            width: 1080,
            height: 1080,
            scale: 3,
            format: 'image/png',
            quality: 1.0,
            backend: RENDER_BACKENDS.HTML2CANVAS,
          });
          zip.file(`stockroom-brand-story-${String(i + 1).padStart(2, '0')}.png`, blob);
        } finally {
          cleanup?.();
        }
      }

      zip.file('CAPTION.txt', BRAND_STORY_CAPTION);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.download = `stockroom-brand-story-carousel-${Date.now()}.zip`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      alert('BRAND_STORY_EXPORT_COMPLETE // 5_SLIDES + CAPTION');
    } catch (e) {
      console.error('BRAND_STORY_EXPORT_ERROR:', e);
      alert('EXPORT_FAILED: ' + e.message);
    } finally {
      iframe.remove();
      setIsDownloading(false);
    }
  };

  const handleDownloadImage = async () => {
    if (selectedTemplate === 'brand_story') {
      return handleDownloadBrandStoryCarousel();
    }

    const targetRef = iframeRef.current || previewRef.current;
    if (!targetRef) return alert('PREVIEW_NOT_READY');
    setIsDownloading(true);

    const w = adViewMode === 'mobile' ? 1080 : adViewMode === 'square' ? 1080 : 1920;
    const h = adViewMode === 'mobile' ? 1350 : adViewMode === 'square' ? 1080 : 1080;

    let cleanup;
    try {
      // Phase 1: deterministically freeze the iframe (fonts + images + layout lock)
      cleanup = await prepareScene(targetRef, { width: w, height: h });

      // Phase 1: render via RenderEngine at 3× DPR for premium print-quality output
      const blob = await renderToImage(targetRef, {
        width  : w,
        height : h,
        scale  : 3,
        format : 'image/png',
        quality: 1.0,
        backend: RENDER_BACKENDS.HTML2CANVAS,
      });

      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `stockroom-manifest-${selectedRetailer || 'ad'}-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      alert('MANIFEST_EXPORT_COMPLETE // CHECK_DOWNLOADS');
    } catch (e) {
      console.error('EXPORT_ERROR:', e);
      alert('EXPORT_FAILED: ' + e.message);
    } finally {
      cleanup?.();
      setIsDownloading(false);
    }
  };

  const copyBrandStoryCaption = async () => {
    try {
      await navigator.clipboard.writeText(BRAND_STORY_CAPTION);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch (e) {
      alert('COPY_FAILED — select caption manually');
    }
  };

  const handleGenerateManifest = () => {
    const bgAsset = (adViewMode === 'mobile' || adViewMode === 'square') ? partnerMobileBanner : partnerDesktopBanner;
    
    if (selectedTemplate === 'official_partner' && bgAsset) {
      const isDynamic = bgAsset.startsWith('data:video') || bgAsset.startsWith('data:image/gif');
      if (isDynamic) {
        handleDownloadVideo();
      } else {
        handleDownloadImage();
      }
    } else {
      handleDownloadImage();
    }
  };

  const handleDownloadVideo = async () => {
    const bgAsset = (adViewMode === 'mobile' || adViewMode === 'square') ? partnerMobileBanner : partnerDesktopBanner;
    if (!bgAsset) return alert('No asset loaded to record.');
    
    setIsRecording(true);
    setRecordingProgress(0);
    
    const theme = COLOR_THEMES[adColorTheme] || COLOR_THEMES.noir;
    
    try {
      const width = adViewMode === 'desktop' ? 1920 : 1080;
      const height = adViewMode === 'mobile' ? 1350 : 1080;
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      let mediaSource;
      const isVideo = bgAsset.startsWith('data:video');
      const isGif = bgAsset.startsWith('data:image/gif');

      if (isVideo) {
        mediaSource = document.createElement('video');
        mediaSource.src = bgAsset;
        mediaSource.crossOrigin = "anonymous";
        mediaSource.muted = true;
        await mediaSource.play();
      } else {
        mediaSource = new Image();
        mediaSource.src = bgAsset;
        await new Promise(r => mediaSource.onload = r);
      }

      const stream = canvas.captureStream(60);
      const recorder = new MediaRecorder(stream, { 
        mimeType: 'video/webm;codecs=vp9', 
        videoBitsPerSecond: 25000000 
      });
      const chunks = [];
      
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stockroom-partner-${selectedRetailer || 'manifest'}-${Date.now()}.mp4`;
        link.click();
        setIsRecording(false);
      };

      recorder.start();

      const startTime = Date.now();
      const duration = 10000; // Record 10 seconds
      let gifStartTime = Date.now();

      const renderFrame = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        setRecordingProgress(Math.min((elapsed / duration) * 100, 100));

        if (elapsed >= duration) {
          recorder.stop();
          if (isVideo) mediaSource.pause();
          return;
        }

        // 1. Draw Background (Video or GIF/Image)
        ctx.fillStyle = theme.background;
        ctx.fillRect(0, 0, width, height);
        
        ctx.globalAlpha = 0.5;
        if (isGif && gifFrames.length > 0) {
          const totalGifDuration = gifFrames.reduce((acc, f) => acc + (f.delay || 100), 0);
          let gifElapsed = (now - gifStartTime) % totalGifDuration;
          
          let frameIndex = 0;
          let sum = 0;
          for (let i = 0; i < gifFrames.length; i++) {
            sum += (gifFrames[i].delay || 100);
            if (gifElapsed < sum) {
              frameIndex = i;
              break;
            }
          }
          ctx.drawImage(gifFrames[frameIndex].canvas, 0, 0, width, height);
        } else {
          ctx.drawImage(mediaSource, 0, 0, width, height);
        }
        ctx.globalAlpha = 1.0;

        // 2. Corners Accents (Matches CSS Logic)
        ctx.strokeStyle = theme.primary || '#00E676';
        ctx.lineWidth = 4;
        const cornerSize = 40;
        const cornerPadding = adViewMode === 'mobile' ? 60 : (adViewMode === 'square' ? 40 : 80);

        // TL
        ctx.beginPath(); ctx.moveTo(cornerPadding, cornerPadding + cornerSize); ctx.lineTo(cornerPadding, cornerPadding); ctx.lineTo(cornerPadding + cornerSize, cornerPadding); ctx.stroke();
        // TR
        ctx.beginPath(); ctx.moveTo(width - cornerPadding - cornerSize, cornerPadding); ctx.lineTo(width - cornerPadding, cornerPadding); ctx.lineTo(width - cornerPadding, cornerPadding + cornerSize); ctx.stroke();
        // BL
        ctx.beginPath(); ctx.moveTo(cornerPadding, height - cornerPadding - cornerSize); ctx.lineTo(cornerPadding, height - cornerPadding); ctx.lineTo(cornerPadding + cornerSize, height - cornerPadding); ctx.stroke();
        // BR
        ctx.beginPath(); ctx.moveTo(width - cornerPadding - cornerSize, height - cornerPadding); ctx.lineTo(width - cornerPadding, height - cornerPadding); ctx.lineTo(width - cornerPadding, height - cornerPadding - cornerSize); ctx.stroke();

        // 3. Typography Logic (Matches Theme)
        ctx.textAlign = 'center';
        
        // Stockroom Background Label (Subtle)
        ctx.fillStyle = theme.signature;
        ctx.globalAlpha = theme.sigOpacity * 0.5;
        ctx.font = '900 280px Montserrat';
        ctx.fillText('STOCKROOM', width / 2, height / 2 + 100);
        ctx.globalAlpha = 1.0;

        // Primary Branding
        ctx.fillStyle = theme.signature;
        ctx.font = '400 24px Montserrat';
        ctx.letterSpacing = '15px';
        ctx.fillText('OFFICIAL_PARTNER', width / 2, height / 2 - 120);

        // Retailer Name (The Hero)
        if (selectedRetailer) {
          ctx.fillStyle = '#00E676';
          ctx.font = '900 120px Montserrat';
          ctx.letterSpacing = '2px';
          ctx.fillText(selectedRetailer.toUpperCase(), width / 2, height / 2 + 20);
          
          // Accent Line under Retailer
          ctx.fillStyle = '#00E676';
          ctx.fillRect(width/2 - 100, height/2 + 60, 200, 4);
        }

        // Branding Footer
        ctx.fillStyle = theme.signature;
        ctx.font = '900 40px Montserrat';
        ctx.letterSpacing = '20px';
        ctx.fillText('STOCKROOM', width / 2, height - 120);

        // Metadata String
        ctx.fillStyle = theme.signature;
        ctx.globalAlpha = 0.3;
        ctx.font = '12px "Roboto Mono", monospace';
        ctx.letterSpacing = '2px';
        ctx.fillText(`MANIFEST_v1.0.4 // SESSION_${startTime.toString().slice(-6)}`, width / 2, height - 80);
        ctx.globalAlpha = 1.0;

        requestAnimationFrame(renderFrame);
      };

      renderFrame();

    } catch (e) {
      console.error('RECORDING_ERROR:', e);
      alert('Recording failed. Check console.');
      setIsRecording(false);
    }
  };

  const SHOWCASE_THEMES = {
    grid: { 
      name: 'GRID_PROTOCOL', 
      primary: '#00E676', 
      pattern: `
        background-image: 
          linear-gradient(rgba(0, 230, 118, 0.15) 2px, transparent 2px),
          linear-gradient(90deg, rgba(0, 230, 118, 0.15) 2px, transparent 2px);
        background-size: 80px 80px;
        filter: drop-shadow(0 0 5px rgba(0,230,118,0.2));
      `
    },
    circuit: { 
      name: 'CIRCUIT_FLOW', 
      primary: '#00E676', 
      pattern: `
        background-image: radial-gradient(rgba(0, 230, 118, 0.2) 3px, transparent 0);
        background-size: 40px 40px;
        filter: drop-shadow(0 0 4px rgba(0, 230, 118, 0.3));
      `
    },
    matrix: { 
      name: 'DATA_MATRIX', 
      primary: '#00E676', 
      pattern: `
        background-image: repeating-linear-gradient(0deg, rgba(0, 230, 118, 0.1) 0px, rgba(0, 230, 118, 0.1) 3px, transparent 3px, transparent 12px);
        filter: drop-shadow(0 0 3px rgba(0, 230, 118, 0.1));
      `
    },
    currency: { 
      name: 'MARKET_SIGNAL', 
      primary: '#00E676', 
      pattern: `
        background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='10' y='40' fill='rgba(0,230,118,0.15)' font-family='monospace' font-size='35' font-weight='900'%3E$%3C/text%3E%3Ctext x='55' y='85' fill='rgba(0,230,118,0.1)' font-family='monospace' font-size='30' font-weight='900'%3E%%3C/text%3E%3C/svg%3E");
        background-size: 200px 200px;
      `
    },
    blueprint: { 
      name: 'BLUEPRINT_CORE', 
      primary: '#00E676', 
      pattern: `
        background-image: 
          linear-gradient(rgba(0, 230, 118, 0.15) 2px, transparent 2px),
          linear-gradient(90deg, rgba(0, 230, 118, 0.15) 2px, transparent 2px),
          linear-gradient(rgba(0, 230, 118, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 230, 118, 0.05) 1px, transparent 1px);
        background-size: 160px 160px, 160px 160px, 40px 40px, 40px 40px;
      `
    }
  };


  const generatedAdHTML = useMemo(() => {
    // Branching theme selection based on template
    const isShowcase = selectedTemplate === 'product_showcase';
    const theme = isShowcase 
      ? (SHOWCASE_THEMES[adColorTheme] || SHOWCASE_THEMES.grid)
      : (COLOR_THEMES[adColorTheme] || COLOR_THEMES.noir);

    const boilerplate = (style, body) => `
<!DOCTYPE html>
<html>
<head>
  <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Montserrat:wght@400;900&family=Roboto+Mono:wght@400;700&family=Syne:wght@800&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body, html { 
      width: 100%; height: 100%; 
      overflow: hidden; 
      background: ${theme.background}; 
      font-family: 'Montserrat', sans-serif; 
    }
    
    /* ANIMATIONS FOR RARITY EFFECTS */
    @keyframes holo-slide {
      0% { background-position: -200% -200%; }
      100% { background-position: 200% 200%; }
    }

    /* RARITY OVERLAYS - STATIC & HIGH VISIBILITY */
    .rarity-overlay {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 1000;
      display: none;
    }

    /* 1. HOLOGRAPHIC SHARDS (STATIC FOIL) */
    .effect-holo {
      display: block;
      background-image: 
        linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 100%),
        repeating-linear-gradient(45deg, rgba(0,230,118,0.1) 0px, rgba(0,230,118,0.1) 2px, transparent 2px, transparent 10px);
      mix-blend-mode: color-dodge;
    }

    /* 2. REVERSE HOLO (HEAVY SHEEN) */
    .effect-reverse-holo {
      display: block;
      background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2) 0%, transparent 70%);
      mix-blend-mode: screen;
      border: 20px solid rgba(0,230,118,0.2);
    }

    /* 3. RAINBOW PRISMATIC (STATIC OVERLAY) */
    .effect-rainbow {
      display: block;
      background: linear-gradient(135deg, 
        rgba(255,0,0,0.1) 0%, 
        rgba(255,255,0,0.1) 20%, 
        rgba(0,255,0,0.1) 40%, 
        rgba(0,255,255,0.1) 60%, 
        rgba(0,0,255,0.1) 80%, 
        rgba(255,0,255,0.1) 100%
      );
      mix-blend-mode: soft-light;
    }

    /* 4. GOLD FLAKE (HEAVY METAL) */
    .effect-gold {
      display: block;
      background-image: 
        radial-gradient(circle at center, #D4AF37 1px, transparent 1.5px),
        linear-gradient(45deg, rgba(212,175,55,0.1) 0%, transparent 100%);
      background-size: 20px 20px, 100% 100%;
      mix-blend-mode: color-dodge;
    }

    /* 5. TEXTURED CARDSTOCK */
    .effect-texture {
      display: block;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
      opacity: 0.2;
      mix-blend-mode: overlay;
    }
    
    /* LINK IN BIO - THEME SENSITIVE */
    .link-in-bio-tag { 
      position: absolute; 
      top: 50%; 
      right: -80px; 
      transform: rotate(-90deg) translateY(-50%); 
      font-family: 'Roboto Mono', monospace; 
      font-size: 14px; 
      font-weight: 700; 
      color: ${theme.linkBio}; 
      border: 3px solid ${theme.linkBio}; 
      padding: 12px 25px; 
      z-index: 100; 
      text-transform: uppercase; 
      background: ${theme.background === '#ffffff' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'}; 
      backdrop-filter: blur(10px); 
    }

    /* STOCKROOM SIGNATURE - THEME SENSITIVE */
    .stockroom-signature { 
      position: absolute; 
      bottom: 50px; 
      left: 50%; 
      transform: translateX(-50%); 
      font-weight: 900; 
      font-size: 58px; 
      letter-spacing: 25px; 
      color: ${theme.signature}; 
      z-index: 90; 
      text-transform: uppercase; 
      opacity: ${theme.sigOpacity}; 
      width: 100%; 
      text-align: center; 
      pointer-events: none;
      display: ${selectedTemplate === 'noir_duality' || selectedTemplate === 'clothing_showcase' || selectedTemplate === 'product_showcase' || selectedTemplate === 'official_partner' ? 'none' : 'block'};
    }
    
    /* PRODUCT INFO - STRICTLY STATIC (STOCKROOM BRANDING) */
    .brand-hdr { font-family: 'Roboto Mono', monospace; font-size: 20px !important; color: #00E676 !important; letter-spacing: 8px; margin-bottom: 20px; text-transform: uppercase; font-weight: 700; }
    .item-title { font-size: 72px; font-weight: 900; line-height: 0.9; margin-bottom: 40px; letter-spacing: -3px; text-transform: uppercase; color: #ffffff !important; }
    .price-line { display: flex; align-items: center; justify-content: space-between; width: 100%; margin-bottom: 50px; }
    .price-wrap { display: flex; align-items: center; gap: 30px; }
    .price-new { font-weight: 900; font-size: 48px; color: #00E676 !important; letter-spacing: -1px; }
    .price-was { text-decoration: line-through; color: #ffffff !important; background: #000000 !important; padding: 4px 12px; font-size: 22px; font-family: 'Roboto Mono', monospace; border: 1px solid #222; }
    .discount-label { background: #00E676 !important; color: #000000 !important; font-weight: 900; padding: 10px 20px; font-size: 24px; font-family: 'Bebas Neue', sans-serif; box-shadow: 0 5px 20px rgba(0,230,118,0.3); white-space: nowrap; }

    /* DUO SPECIFIC (CLOTHING SHOWCASE) - STRICTLY STATIC */
    .duo-brand { font-size: 14px; color: #00E676 !important; letter-spacing: 5px; margin-bottom: 10px; font-weight: 700; text-transform: uppercase; }
    .duo-name { font-size: 32px; font-weight: 900; line-height: 0.9; margin-bottom: 25px; text-transform: uppercase; color: #ffffff !important; }
    .duo-prices { display: flex; align-items: center; justify-content: space-between; width: 100%; }
    .duo-price-wrap { display: flex; align-items: center; gap: 20px; white-space: nowrap; }
    .duo-was { font-size: 14px; color: #ffffff !important; background: #000000 !important; padding: 4px 12px; text-decoration: line-through; text-decoration-color: #00E676; font-family: 'Roboto Mono', monospace; border: 1px solid #222; }
    .duo-now { font-size: 32px; font-weight: 900; color: #00E676 !important; }
    .duo-pct { background: #00E676 !important; color: #000000 !important; padding: 8px 15px; font-weight: 900; font-size: 28px; font-family: 'Bebas Neue', sans-serif; white-space: nowrap; }

    ${style}
  </style>
</head>
<body>
  <div class="link-in-bio-tag">LINK IN BIO</div>
  ${body}
  <div class="stockroom-signature">STOCKROOM</div>
</body>
</html>`;

    let finalHtml = '';

    if (selectedTemplate === 'brand_story') {
      finalHtml = buildBrandStorySlideHTML(brandStorySlideIndex);
    } else if (selectedTemplate === 'blank_canvas') {
      const heroProduct = selectedAdProducts[0] || curationPool[0] || null;
      const schema = buildDealSignalSchema({
        product: heroProduct,
        totalSavings: savingsStats?.totalSavings || 0,
        avgSavingsPct: savingsStats?.averageSavingsPercentage || 0,
        dealsLive: savingsStats?.totalDeals || 0,
        format: adViewMode === 'square' ? '1:1' : (adViewMode === 'desktop' ? '21:9' : '4:5'),
      });
      finalHtml = renderToHTML(schema);
    } else if (selectedTemplate === 'product_showcase') {
      const p = selectedAdProducts[0] || curationPool[0] || { name: 'SELECTED_PRODUCT', brand: 'STOCKROOM', price: '0.00', images: [''] };
      const hasDiscount = p.originalPrice && p.originalPrice > p.price;
      const discount = p.discountPercentage || (hasDiscount ? Math.round((1 - p.price / p.originalPrice) * 100) : 0);
      const retailer = selectedRetailer || p.brand || 'STOCKROOM';

      finalHtml = boilerplate(`
        body, html {
          background: #000 !important;
          ${theme.pattern}
        }

        .showcase-master { 
          width: 100%; height: 100%; 
          display: flex; align-items: center; justify-content: center;
          background: transparent !important;
        }
        
        /* HIDE GLOBAL TAG & SIGNATURE */
        .link-in-bio-tag { display: none !important; }
        .stockroom-signature { display: none !important; }

        .pokemon-card {
          width: 100%;
          height: 100%;
          background: #000;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          border: 12px solid ${theme.primary};
        }

        .overlay-effect {
          position: absolute;
          inset: 0;
          z-index: 150;
          pointer-events: none;
          ${theme.pattern}
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 40px 40px 20px 40px;
          z-index: 110;
          background: rgba(0,0,0,0.5); 
          backdrop-filter: blur(8px);
        }

        .card-name {
          font-size: 52px;
          font-weight: 900;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: -2.5px;
          text-shadow: 3px 3px 0 #000;
        }

        .card-hp {
          background: ${theme.primary};
          color: #000;
          padding: 10px 25px;
          font-size: 38px;
          font-weight: 900;
          font-family: 'Bebas Neue', sans-serif;
          box-shadow: 8px 8px 0 #000;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #000;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .image-frame {
          width: 100%;
          flex: 12; 
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: relative;
          border-top: 4px solid #1a1a1a;
          border-bottom: 4px solid #1a1a1a;
          z-index: 200; /* Higher than overlay-effect */
        }

        .product-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .local-link-tag {
          position: absolute;
          bottom: 0;
          right: 0;
          font-family: 'Roboto Mono', monospace;
          font-size: 18px;
          font-weight: 900;
          color: ${theme.primary};
          border: 3px solid ${theme.primary};
          padding: 15px 35px;
          background: #000;
          z-index: 300;
          text-transform: uppercase;
        }

        .ability-section {
          flex: 0.3;
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 20px 40px 40px 40px;
          z-index: 110;
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(8px);
        }

        .ability {
          padding-bottom: 10px;
        }

        .ability-title {
          color: #fff;
          font-weight: 900;
          font-size: 26px; /* half of the price size */
          margin-bottom: 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          letter-spacing: -1px;
          gap: 40px;
        }

        .price-display {
          font-size: 52px;
          font-weight: 900;
          font-family: 'Roboto Mono', monospace;
          letter-spacing: -2px;
          color: ${theme.primary};
          text-shadow: 4px 4px 0 #000;
        }

        .energy-orb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: ${theme.primary};
          border: 4px solid #000;
        }
      `, `
        <div class="showcase-master">
          <div class="pokemon-card">
            <div class="overlay-effect"></div>
            
            <div class="card-header">
              <div class="card-name">${p.brand?.toUpperCase() || 'STOCKROOM'}</div>
              <div class="card-hp">-${discount}% OFF</div>
            </div>

            <div class="image-frame">
              <img src="${p.image || p.images?.[0]}" class="product-img" />
              <div class="local-link-tag">LINK IN BIO</div>
            </div>

            <div class="ability-section">
              <div class="ability">
                <div class="ability-title">
                  <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
                    <div style="display: flex; gap: 6px;">
                      <div class="energy-orb"></div>
                      <div class="energy-orb"></div>
                    </div>
                    <span>${p.name.toUpperCase()}</span>
                  </div>
                  <span class="price-display">$${p.price}</span>
                </div>
              </div>
              
              <div class="ability" style="border: none;">
                <div class="ability-title">
                  <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
                    <div style="display: flex; gap: 6px;">
                      <div class="energy-orb"></div>
                      <div class="energy-orb" style="background: #fff;"></div>
                    </div>
                    <span>ORIGINAL PRICE</span>
                  </div>
                  <span class="price-display" style="color: #fff; text-decoration: line-through">$${p.originalPrice || p.price}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `);




    } else if (selectedTemplate === 'noir_duality' || selectedTemplate === 'clothing_showcase') {
      finalHtml = boilerplate(`
        .ad-container { width: 100%; height: 100%; display: flex; flex-direction: column; background: ${theme.background}; position: relative; }
        .link-in-bio-tag { top: 40px; left: 40px; right: auto; bottom: auto; transform: none; }
        .duo-grid { flex: 1; display: grid; grid-template-columns: 1fr 1fr; }
        .duo-panel { position: relative; overflow: hidden; border: 2px solid ${theme.background}; }
        .duo-img-cont { width: 100%; height: 100%; background-size: cover; background-position: center; position: relative; }
        .duo-overlay-meta { position: absolute; bottom: 0; left: 0; right: 0; padding: 60px; background: linear-gradient(to top, ${theme.background === '#ffffff' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'} 0%, transparent 100%); }
        
        .center-badge {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #000000;
          border: 2px solid #00E676;
          color: #00E676;
          padding: 10px 30px;
          font-weight: 900;
          font-size: 20px;
          letter-spacing: 8px;
          text-transform: uppercase;
          z-index: 500;
          box-shadow: 0 0 30px rgba(0,0,0,0.5);
          white-space: nowrap;
        }
      `, `
        <div class="ad-container">
          <div class="duo-grid">${generateDuoNodes(selectedAdProducts)}</div>
          <div class="center-badge">STOCKROOM</div>
        </div>
      `);
    } else if (selectedTemplate === 'single_product_shoes') {
      const p = selectedAdProducts[0] || curationPool[0] || { name: 'SELECTED_PRODUCT', brand: 'STOCKROOM', price: '0.00', images: [''] };
      const hasDiscount = p.originalPrice && p.originalPrice > p.price;
      const discount = p.discountPercentage || (hasDiscount ? Math.round((1 - p.price / p.originalPrice) * 100) : 0);
      finalHtml = boilerplate(`
        .single-product-master { width: 100%; height: 100%; position: relative; background: #fff; overflow: hidden; }
        .full-bleed-img { 
          width: 100%; height: 100%; 
          object-fit: cover; 
          position: absolute; 
          inset: 0; 
          z-index: 10;
        }
        
        .top-branding-block {
          position: absolute;
          top: 60px;
          left: 12%; /* increased from 5% to bring info closer together */
          right: 12%; /* increased from 5% to bring info closer together */
          display: flex;
          flex-direction: column;
          align-items: center;
          z-index: 100;
          background: transparent !important;
        }

        .branding-header {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 80px;
          background: transparent !important;
        }

        .logo-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #000;
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #00E676;
          box-shadow: none !important;
        }

        .branding-text {
          font-weight: 900;
          font-size: 32px;
          color: #000;
          letter-spacing: 8px;
          text-transform: uppercase;
        }

        .logo-circle img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .product-info-grid {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          width: 100%;
          gap: 40px;
        }

        .product-metadata {
          flex: 1;
          text-align: left;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 5px;
          background: transparent !important;
        }

        .minimal-brand {
          font-weight: 900;
          font-size: 52px;
          color: #000;
          text-transform: uppercase;
          letter-spacing: -2px;
          line-height: 1;
          text-shadow: none !important;
        }

        .minimal-name {
          font-weight: 400;
          font-size: 18px;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 8px;
          text-shadow: none !important;
          line-height: 1.4;
        }

        .pricing-stack {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          text-align: right;
        }

        .sale-price {
          font-weight: 900;
          font-size: 82px;
          color: #00E676;
          letter-spacing: -3px;
          text-shadow: none !important;
          line-height: 0.8;
        }

        .was-price {
          font-weight: 700;
          font-size: 32px;
          color: #000;
          text-decoration: line-through;
          font-family: 'Roboto Mono', monospace;
          margin-bottom: 10px;
          opacity: 0.5;
        }

        .link-in-bio-tag { display: none !important; }
        .stockroom-signature { display: none !important; }
      `, `
        <div class="single-product-master">
          <img src="${p.image || p.images?.[0]}" class="full-bleed-img" />
          <div class="top-branding-block">
            <div class="branding-header">
              <div class="logo-circle">
                <img src="/images/logos/IGLogo.png" alt="" />
              </div>
              <span class="branding-text">STOCKROOM</span>
            </div>
            
            <div class="product-info-grid">
              <div class="product-metadata">
                <div class="minimal-brand">${p.brand?.toUpperCase() || 'STOCKROOM'}</div>
                <div class="minimal-name">${p.name.toUpperCase()}</div>
              </div>
              
              <div class="pricing-stack">
                ${hasDiscount ? `<span class="was-price">$${p.originalPrice}</span>` : ''}
                <span class="sale-price">$${p.price}</span>
              </div>
            </div>
          </div>
        </div>
      `);
    } else if (selectedTemplate === 'single_product_clothing') {
      const p = selectedAdProducts[0] || curationPool[0] || { name: 'SELECTED_PRODUCT', brand: 'STOCKROOM', price: '0.00', images: [''] };
      const escapeHTML = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      const formatPrice = (value) => {
        const raw = String(value ?? '').trim();
        const numeric = Number(raw.replace(/[^0-9.]/g, ''));
        if (!raw) return '$0';
        if (!Number.isFinite(numeric)) return raw;
        return `$${numeric % 1 === 0 ? numeric.toFixed(0) : numeric.toFixed(2)}`;
      };
      const imageSrc = p.image || p.images?.[0] || '';
      const brand = escapeHTML(p.brand || p.retailer || selectedRetailer || 'STOCKROOM');
      const productName = escapeHTML(p.name || 'Selected Product');
      const price = formatPrice(p.price);
      const hasDiscount = p.originalPrice && Number(p.originalPrice) > Number(p.price);
      const originalPrice = hasDiscount ? formatPrice(p.originalPrice) : '';
      const discount = p.discountPercentage || (hasDiscount ? Math.round((1 - Number(p.price) / Number(p.originalPrice)) * 100) : 0);
      finalHtml = boilerplate(`
        .single-product-master {
          width: 100%;
          height: 100%;
          background: #f7f7f3;
          overflow: hidden;
          position: relative;
        }
        .full-bleed-img {
          position: absolute;
          inset: 0;
          background-image: url('${imageSrc}');
          background-size: cover;
          background-position: center top;
          background-repeat: no-repeat;
          z-index: 1;
        }
        .image-polish {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          background:
            linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.02) 22%, rgba(0,0,0,0) 52%),
            linear-gradient(0deg, rgba(0,0,0,0.74) 0%, rgba(0,0,0,0.42) 27%, rgba(0,0,0,0) 54%);
        }
        .top-rail {
          position: absolute;
          top: ${adViewMode === 'mobile' ? '58px' : '54px'};
          left: ${adViewMode === 'mobile' ? '58px' : '70px'};
          right: ${adViewMode === 'mobile' ? '58px' : '70px'};
          z-index: 4;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #fff;
          text-shadow: 0 2px 16px rgba(0,0,0,0.28);
        }
        .stockroom-mark {
          font-family: 'Montserrat', sans-serif;
          font-size: ${adViewMode === 'mobile' ? '13px' : '16px'};
          font-weight: 900;
          letter-spacing: ${adViewMode === 'mobile' ? '7px' : '9px'};
          text-transform: uppercase;
        }
        .template-label {
          font-family: 'Roboto Mono', monospace;
          font-size: ${adViewMode === 'mobile' ? '9px' : '11px'};
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.76);
        }
        .product-meta {
          position: absolute;
          left: ${adViewMode === 'mobile' ? '58px' : '72px'};
          right: ${adViewMode === 'mobile' ? '58px' : '72px'};
          bottom: ${adViewMode === 'mobile' ? '58px' : '62px'};
          z-index: 4;
          color: #fff;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: ${adViewMode === 'mobile' ? '24px' : '38px'};
          align-items: end;
        }
        .brand-name {
          font-family: 'Roboto Mono', monospace;
          font-size: ${adViewMode === 'mobile' ? '15px' : '18px'};
          font-weight: 700;
          letter-spacing: 3.2px;
          text-transform: uppercase;
          color: #00E676;
          margin-bottom: 14px;
        }
        .product-name {
          font-family: 'Montserrat', sans-serif;
          font-size: ${adViewMode === 'mobile' ? '48px' : '72px'};
          font-weight: 900;
          line-height: 0.92;
          letter-spacing: -2.2px;
          max-width: ${adViewMode === 'mobile' ? '700px' : '1120px'};
          text-transform: uppercase;
          text-shadow: 0 2px 22px rgba(0,0,0,0.35);
        }
        .price-cluster {
          width: ${adViewMode === 'mobile' ? '320px' : '430px'};
          min-height: ${adViewMode === 'mobile' ? '190px' : '240px'};
          padding: ${adViewMode === 'mobile' ? '25px 28px' : '32px 36px'};
          border: 2px solid rgba(255,255,255,0.82);
          border-radius: 20px;
          background: rgba(8,8,8,0.68);
          backdrop-filter: blur(14px);
          text-align: left;
          box-shadow: 0 18px 44px rgba(0,0,0,0.24);
          position: relative;
          overflow: hidden;
        }
        .price-cluster::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 6px;
          background: #00E676;
        }
        .price-cluster::after {
          content: '';
          position: absolute;
          inset: 0;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 18px;
          pointer-events: none;
        }
        .price-label {
          font-family: 'Roboto Mono', monospace;
          font-size: ${adViewMode === 'mobile' ? '12px' : '15px'};
          color: #00E676;
          letter-spacing: 3.6px;
          text-transform: uppercase;
          margin: 10px 0 13px;
        }
        .now-price {
          font-family: 'Montserrat', sans-serif;
          font-size: ${adViewMode === 'mobile' ? '76px' : '96px'};
          font-weight: 900;
          line-height: 0.88;
          letter-spacing: -3px;
          margin-bottom: 18px;
        }
        .price-divider {
          height: 1px;
          background: rgba(255,255,255,0.22);
          margin-bottom: 15px;
        }
        .was-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 14px;
          font-family: 'Roboto Mono', monospace;
          font-size: ${adViewMode === 'mobile' ? '14px' : '17px'};
          color: rgba(255,255,255,0.76);
        }
        .was-price { text-decoration: line-through; }
        .discount-chip {
          color: #111;
          background: #00E676;
          border-radius: 999px;
          padding: 7px 11px;
          font-weight: 700;
        }
        .link-in-bio-tag { display: none !important; }
        .stockroom-signature { display: none !important; }
      `, `
        <div class="single-product-master">
          <div class="full-bleed-img"></div>
          <div class="image-polish"></div>
          <div class="top-rail">
            <div class="stockroom-mark">THE STOCKROOM</div>
            <div class="template-label">FEATURED PIECE</div>
          </div>
          <div class="product-meta">
            <div>
              <div class="brand-name">${brand}</div>
              <div class="product-name">${productName}</div>
            </div>
            <div class="price-cluster">
              <div class="price-label">Current Price</div>
              <div class="now-price">${price}</div>
              ${hasDiscount ? `
                <div class="price-divider"></div>
                <div class="was-row">
                  <span class="was-price">${originalPrice}</span>
                  ${discount > 0 ? `<span class="discount-chip">-${discount}%</span>` : ''}
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `);
    } else if (selectedTemplate === 'official_partner') {
      const bgAsset = (adViewMode === 'mobile' || adViewMode === 'square') ? partnerMobileBanner : partnerDesktopBanner;
      const cornerPadding = adViewMode === 'mobile' ? '60px' : (adViewMode === 'square' ? '40px' : '80px');
      const retailerSize = `${partnerFontSize}vw`;
      
      finalHtml = boilerplate(`
        body, html { background: ${theme.background}; }
        .link-in-bio-tag { display: none !important; }
        .asset-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.5; }
        .overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px; color: ${theme.signature}; text-align: center; z-index: 10; }
        .corner { position: absolute; width: 40px; height: 40px; border: 4px solid ${theme.linkBio}; }
        .tl { top: ${cornerPadding}; left: ${cornerPadding}; border-right: none; border-bottom: none; }
        .tr { top: ${cornerPadding}; right: ${cornerPadding}; border-left: none; border-bottom: none; }
        .bl { bottom: ${cornerPadding}; left: ${cornerPadding}; border-right: none; border-top: none; }
        .br { bottom: ${cornerPadding}; right: ${cornerPadding}; border-left: none; border-top: none; }
        .bg-text { position: absolute; font-size: 15vw; font-weight: 900; color: ${theme.signature}08; z-index: -1; white-space: nowrap; pointer-events: none; }
        .label { font-size: 2vw; letter-spacing: 1.5vw; margin-bottom: 4vh; font-weight: 400; color: ${theme.signature}; }
        .retailer { 
          font-size: ${retailerSize}; 
          font-weight: 900; 
          color: #00E676; 
          margin-bottom: 1vh; 
          line-height: 1.1; 
          max-width: 80%; 
          word-wrap: break-word; 
        }
        .line { width: 10vw; height: 0.5vh; background: #00E676; margin: 2vh auto; }
      `, `
        ${bgAsset && bgAsset.startsWith('data:video') ? `<video class="asset-bg" src="${bgAsset}" autoplay muted loop></video>` : `<img class="asset-bg" src="${bgAsset || ''}" />`}
        <div class="overlay">
          <div class="corner tl"></div><div class="corner tr"></div>
          <div class="corner bl"></div><div class="corner br"></div>
          <div class="bg-text">STOCKROOM</div>
          <div class="label">OFFICIAL_PARTNER</div>
          <div class="retailer">${(partnerCustomText || selectedRetailer || 'PARTNER').toUpperCase()}</div>
          <div class="line"></div>
        </div>
      `);
    } else if (selectedTemplate === 'ad_creative') {
      const p = selectedAdProducts[0];

      if (p) {
        const formatPrice = (value) => {
          const n = Number(value);
          if (Number.isNaN(n)) return '$0';
          return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
        };
        const hasDiscount = p.originalPrice && Number(p.originalPrice) > Number(p.price);
        const retailPrice = formatPrice(p.originalPrice || p.price);
        const stockroomPrice = formatPrice(p.price);
        const discount = p.discountPercentage || (hasDiscount ? Math.round((1 - Number(p.price) / Number(p.originalPrice)) * 100) : 0);
        const productImage = p.image || p.images?.[0] || '';
        const categoryHint = (p.categories?.[0] || p.category || '').toString().toLowerCase();
        const valueLine = (() => {
          if (discount >= 30) return 'Limited Drop Release';
          if (p.trending) return 'Best Seller This Week';
          if (categoryHint.includes('hoodie') || categoryHint.includes('sweater') || categoryHint.includes('cotton')) return 'Heavyweight Cotton Build';
          if (categoryHint.includes('shoe') || categoryHint.includes('sneaker') || categoryHint.includes('boot')) return 'Premium Streetwear Fit';
          if (categoryHint.includes('jacket') || categoryHint.includes('outerwear') || categoryHint.includes('coat')) return 'Premium Outerwear Cut';
          if (categoryHint) return `Premium ${(p.categories?.[0] || p.category).toString()} Fit`;
          return 'Limited Stock Drop';
        })();

        const isMobile = adViewMode === 'mobile';
        const isDesktop = adViewMode === 'desktop';
        const leftLabelSize = isMobile ? '8px' : isDesktop ? '10px' : '9px';
        const leftPriceSize = isMobile ? '22px' : isDesktop ? '36px' : '26px';
        const offerLabelSize = isMobile ? '10px' : isDesktop ? '14px' : '11px';
        const heroPriceSize = isMobile ? '88px' : isDesktop ? '152px' : '104px';
        const badgeSize = isMobile ? '15px' : isDesktop ? '24px' : '18px';
        const valueLineSize = isMobile ? '9px' : isDesktop ? '12px' : '10px';
        const panelPad = isMobile ? '28px 20px' : isDesktop ? '48px 56px' : '36px 32px';
        const bandWidth = isMobile ? '10px' : '12px';
        const leftWidth = '46%';

        finalHtml = boilerplate(`
        .link-in-bio-tag, .stockroom-signature { display: none !important; }

        .split-ad {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: #000;
        }

        .split-product-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 0;
        }

        .split-overlay-left {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          width: ${leftWidth};
          z-index: 1;
          pointer-events: none;
          background: rgba(45, 55, 72, 0.28);
          -webkit-backdrop-filter: grayscale(35%) brightness(0.88) blur(0.4px);
          backdrop-filter: grayscale(35%) brightness(0.88) blur(0.4px);
        }

        .split-band {
          position: absolute;
          top: 0;
          bottom: 0;
          left: ${leftWidth};
          width: ${bandWidth};
          background: linear-gradient(to right, #777777 0%, rgba(120, 120, 120, 0.55) 40%, rgba(0, 230, 118, 0.5) 100%);
          z-index: 2;
          pointer-events: none;
        }

        .split-overlay-right {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: calc(${leftWidth} + ${bandWidth});
          z-index: 1;
          pointer-events: none;
          background: rgba(0, 230, 118, 0.17);
          -webkit-backdrop-filter: contrast(1.08) brightness(1.06) saturate(1.08);
          backdrop-filter: contrast(1.08) brightness(1.06) saturate(1.08);
        }

        .split-ui {
          position: absolute;
          inset: 0;
          z-index: 10;
          display: grid;
          grid-template-columns: ${leftWidth} 1fr;
          align-items: end;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .split-panel-left {
          align-self: end;
          justify-self: start;
          text-align: left;
          padding: ${panelPad};
          box-sizing: border-box;
          max-width: 42%;
        }

        .split-panel-right {
          align-self: end;
          justify-self: end;
          text-align: right;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          padding: ${panelPad};
          box-sizing: border-box;
          max-width: 52%;
        }

        .split-ref-block {
          opacity: 0.7;
        }

        .split-price-label-left {
          font-family: 'Roboto Mono', monospace;
          font-size: ${leftLabelSize};
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 6px;
          color: #888888;
          font-weight: 500;
        }

        .split-price-left {
          font-size: ${leftPriceSize};
          font-weight: 700;
          color: #999999;
          text-decoration: line-through;
          text-decoration-color: #777777;
          text-decoration-thickness: 2px;
          letter-spacing: -0.005em;
          line-height: 1;
        }

        .split-offer-card {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: ${isMobile ? '8px' : '12px'};
          padding: ${isMobile ? '18px 22px' : isDesktop ? '28px 36px' : '22px 28px'};
          border-radius: ${isMobile ? '16px' : '22px'};
          background: rgba(8, 12, 10, 0.62);
          -webkit-backdrop-filter: blur(14px) saturate(1.2);
          backdrop-filter: blur(14px) saturate(1.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow:
            0 0 48px rgba(0, 230, 118, 0.25),
            0 12px 40px rgba(0, 0, 0, 0.45),
            inset 0 1px 0 rgba(255, 255, 255, 0.06);
        }

        .split-offer-label {
          font-family: 'Roboto Mono', monospace;
          font-size: ${offerLabelSize};
          font-weight: 700;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: #00E676;
          line-height: 1;
        }

        .split-price-hero {
          font-size: ${heroPriceSize};
          font-weight: 900;
          color: #ffffff;
          letter-spacing: -0.01em;
          line-height: 0.92;
          text-shadow:
            0 0 32px rgba(0, 230, 118, 0.25),
            0 4px 24px rgba(0, 0, 0, 0.55);
        }

        .split-save-badge {
          display: inline-block;
          background: #00E676;
          color: #000000;
          font-family: 'Bebas Neue', sans-serif;
          font-size: ${badgeSize};
          font-weight: 900;
          letter-spacing: 2px;
          padding: ${isMobile ? '7px 16px' : '9px 20px'};
          line-height: 1;
          box-shadow: 0 4px 24px rgba(0, 230, 118, 0.45);
        }

        .split-value-line {
          font-family: 'Roboto Mono', monospace;
          font-size: ${valueLineSize};
          font-weight: 500;
          letter-spacing: 1.5px;
          color: rgba(255, 255, 255, 0.82);
          text-transform: uppercase;
          margin-top: ${isMobile ? '2px' : '4px'};
          line-height: 1.3;
        }
      `, `
        <div class="split-ad">
          <img class="split-product-bg" src="${productImage}" alt="" />
          <div class="split-overlay-left"></div>
          <div class="split-band"></div>
          <div class="split-overlay-right"></div>
          <div class="split-ui">
            <div class="split-panel-left">
              <div class="split-ref-block">
                <div class="split-price-label-left">Retail Price</div>
                <div class="split-price-left">${retailPrice}</div>
              </div>
            </div>
            <div class="split-panel-right">
              <div class="split-offer-card">
                <div class="split-offer-label">YOUR PRICE</div>
                <div class="split-price-hero">${stockroomPrice}</div>
                ${discount > 0 ? `<div class="split-save-badge">Save ${discount}%</div>` : `<div class="split-save-badge">Limited Drop</div>`}
                <div class="split-value-line">${valueLine}</div>
              </div>
            </div>
          </div>
        </div>
      `);
      } else {
        const padding = adViewMode === 'mobile' ? '40px' : (adViewMode === 'square' ? '40px' : '80px');
        finalHtml = boilerplate(`
        .ad-container { width: 100%; height: 100%; padding: ${padding}; display: flex; flex-direction: column; align-items: center; justify-content: center; background-color: ${theme.background}; text-align: center; }
        .top-meta { position: absolute; top: 40px; width: 100%; display: flex; justify-content: space-between; padding: 0 ${adViewMode === 'mobile' ? '40px' : '80px'}; font-family: 'Roboto Mono', monospace; font-size: 12px; color: #00E676; }
        .main-content { display: flex; flex-direction: column; gap: 20px; align-items: center; }
        .access-tag { font-family: 'Syne', sans-serif; font-weight: 800; font-size: ${adViewMode === 'mobile' ? '14px' : '20px'}; color: #00E676; border: 1px solid #00E676; padding: 5px 20px; letter-spacing: 5px; margin-bottom: 20px; }
        .massive-title { font-weight: 900; font-size: ${adViewMode === 'mobile' ? '60px' : '140px'}; line-height: 0.8; letter-spacing: -5px; color: ${theme.signature}; margin: 0; }
        .sub-title { font-family: 'Bebas Neue', sans-serif; font-size: ${adViewMode === 'mobile' ? '32px' : '72px'}; color: #00E676; letter-spacing: 10px; margin-top: 10px; }
        .data-block { margin-top: 60px; display: grid; grid-template-columns: ${adViewMode === 'mobile' ? '1fr' : 'repeat(3, 1fr)'}; gap: 40px; width: 100%; max-width: 1000px; }
        .stat-item { border-top: 1px solid ${theme.background === '#ffffff' ? '#eee' : '#222'}; padding-top: 20px; }
        .stat-label { font-family: 'Roboto Mono', monospace; font-size: 10px; color: ${theme.background === '#ffffff' ? '#999' : '#444'}; margin-bottom: 10px; }
        .stat-value { font-size: 32px; font-weight: 900; color: ${theme.signature}; }
      `, `
        <div class="ad-container">
          <div class="main-content"><div class="access-tag">AUTHENTICITY_VERIFIED</div><h1 class="massive-title">SIGNAL_DETECTED</h1><div class="sub-title">THE_MANIFEST_IS_LIVE</div>
            <div class="data-block">
              <div class="stat-item"><div class="stat-label">MARKET_SYNC</div><div class="stat-value">OPTIMAL</div></div>
              <div class="stat-item"><div class="stat-label">CURATION_LOGIC</div><div class="stat-value">STRICT_30%</div></div>
              <div class="stat-item"><div class="stat-label">ENCRYPTION</div><div class="stat-value">AES_256</div></div>
            </div>
          </div>
        </div>
      `);
      }
    }

    return finalHtml;
  }, [selectedAdProducts, selectedTemplate, adViewMode, partnerDesktopBanner, partnerMobileBanner, selectedRetailer, curationPool, adColorTheme, savingsStats, partnerCustomText, partnerFontSize, brandStorySlideIndex, adPosition]);



  // Debounced effect to sync state with memoized value
  useEffect(() => {
    const handler = setTimeout(() => {
      setAdStyleCode(generatedAdHTML);
    }, 400); // 400ms debounce
    return () => clearTimeout(handler);
  }, [generatedAdHTML]);

  useEffect(() => {
    if (selectedTemplate === 'brand_story') {
      setAdViewMode('square');
    } else if (selectedTemplate === 'blank_canvas') {
      setAdViewMode('mobile'); // Meta feed 4:5
    }
  }, [selectedTemplate]);


  const categories = ['T-Shirts', 'Hoodies', 'Pants', 'Shorts', 'Jeans', 'Sweaters', 'Jackets', 'Blazers', 'Dresses', 'Skirts', 'Outerwear', 'Activewear', 'Swimwear', 'Shoes', 'Accessories'];

  useEffect(() => {
    if (!currentUser || (currentUser.accountType !== 'admin_retailer' && currentUser.accountType !== 'retailer')) {
      navigate('/signin');
    } else {
      fetchData();
    }
  }, [currentUser, selectedTab]);

  useEffect(() => {
    if (selectedTab === 'advertising') {
      const delayDebounceFn = setTimeout(() => {
        if (adPage !== 1) {
          setAdPage(1);
        } else {
          fetchAdProducts();
        }
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [adSearchTerm, selectedTab]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setShowAdOverlay(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    if (showAdOverlay) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showAdOverlay]);

  const fetchData = async () => {
    if (selectedTab === 'analytics') {
      fetchReports();
      fetchAdminReport();
    }
    if (selectedTab === 'banners') {
      fetchPromotions();
      fetchBrands();
      fetchSiteRetailers();
    }
    if (selectedTab === 'retailer-hub') {
      fetchApiKeys();
      fetchSyncLogs();
      fetchBrands();
      fetchAllRetailers();
      fetchAdminReport();
    }
    if (selectedTab === 'ingestion-engine') {
      fetchBrands();
      fetchDiscovery();
      fetchReports();
      fetchCollisions();
    }
    if (selectedTab === 'conflict') fetchCollisions();
    if (selectedTab === 'advertising') {
      fetchBrands();
      fetchAllRetailers();
      fetchAdProducts();
      fetchCurationPool();
      fetchPromotions();
      fetchSavingsStats();
    }
  };

  const fetchSavingsStats = async () => {
    try {
      const res = await api.get('/api/products/stats');
      setSavingsStats(res.data);
    } catch (e) { console.error('SAVINGS_STATS_FETCH_ERROR:', e); }
  };

  const fetchCurationPool = async () => {
    try {
      // Fetch 100 mixed products AND 50 specific shoes to guarantee variety for the AI engine
      const [mixedRes, shoeRes] = await Promise.all([
        api.get('/api/products', { params: { limit: 100 } }),
        api.get('/api/products', { params: { limit: 50, categories: 'Shoes' } })
      ]);
      
      const combined = [...(mixedRes.data.products || []), ...(shoeRes.data.products || [])];
      // De-duplicate by ID
      const unique = Array.from(new Map(combined.map(p => [p._id, p])).values());
      
      setCurationPool(unique);
    } catch (e) { console.error('CURATION_POOL_FETCH_ERROR:', e); }
  };

  const fetchAdProducts = async () => {
    try {
      const params = {
        limit: 200,
        page: adPage,
        search: adSearchTerm,
        brand: adFilters.brand.join(','),
        retailers: adFilters.retailer.join(','),
        categories: adFilters.category.join(','),
        gender: adFilters.gender.join(',')
      };

      // Map sort config to backend if possible
      if (adSortConfig) {
        if (adSortConfig.key === 'price') {
          params.sort = adSortConfig.direction === 'asc' ? 'PriceLowToHigh' : 'PriceHighToLow';
        } else if (adSortConfig.key === 'calculatedDiscount') {
          params.sort = 'DiscountHighToLow'; // Backend only supports high to low
        }
      }

      const res = await api.get('/api/products', { params });
      setAdProducts(res.data.products || []);
      setAdTotalPages(res.data.pages || 1);
    } catch (e) { console.error('AD_PRODUCTS_FETCH_ERROR:', e); }
  };

  useEffect(() => {
    if (selectedTab === 'advertising' && adSubTab === 'config') {
      setAdPage(1); // Reset to page 1 when filters or search change
    }
  }, [adFilters, adSearchTerm]);

  useEffect(() => {
    if (selectedTab === 'advertising' && adSubTab === 'config') {
      fetchAdProducts();
    }
  }, [adPage, adFilters, adSortConfig]);

  const fetchAdminReport = async (range) => {
    setAnalyticsLoading(true);
    try {
      const r = range || analyticsRange;
      const res = await api.get('/api/products/admin-report', { params: { range: r } });
      setAdminReport(res.data);
    } catch (e) { console.error('ADMIN_REPORT_ERROR:', e); }
    finally { setAnalyticsLoading(false); }
  };

  const fetchDiscovery = async () => {
    try {
      const res = await api.get('/api/integrations/discover');
      setDiscoveredMerchants(Array.isArray(res.data) ? res.data : []);
    } catch (e) { 
      console.error('DISCOVERY_ERROR:', e);
      setDiscoveredMerchants([]);
    }
  };

  const fetchCollisions = async () => {
    try {
      const res = await api.get('/api/integrations/collisions');
      setCollisions(Array.isArray(res.data) ? res.data : []);
    } catch (e) { 
      console.error('COLLISION_ERROR:', e);
      setCollisions([]);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await api.get('/api/admin/retailers/reports');
      setReports(res.data);
    } catch (e) { console.error('Error fetching reports:', e); }
  };

  const fetchPromotions = async () => {
    try {
      const res = await api.get('/api/promotions/admin');
      setPromotions(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Error fetching promotions:', e); }
  };

  const fetchApiKeys = async () => {
    try {
      const res = await api.get('/api/integrations/keys');
      setApiKeys(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Error fetching API keys:', e); }
  };

  const fetchSyncLogs = async () => {
    try {
      const res = await api.get('/api/integrations/logs');
      setSyncLogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Error fetching sync logs:', e); }
  };

  const fetchBrands = async () => {
    try {
      const res = await api.get('/api/admin/retailers/brands');
      setActiveBrands(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Error fetching brands:', e); }
  };

  const fetchSiteRetailers = async () => {
    try {
      const res = await api.get('/api/products/retailers');
      setSiteRetailers(Array.isArray(res.data) ? res.data.filter(Boolean) : []);
    } catch (e) { console.error('Error fetching site retailers:', e); }
  };

  const fetchAllRetailers = async () => {
    try {
      console.log('Fetching all retailers...');
      const [listRes, brandsRes] = await Promise.all([
        api.get('/api/retailer-list').catch(err => { console.warn('retailer-list failed', err); return { data: [] }; }),
        api.get('/api/admin/retailers/brands').catch(err => { console.warn('brands failed', err); return { data: [] }; })
      ]);

      const listData = Array.isArray(listRes.data) ? listRes.data : [];
      const brandsData = Array.isArray(brandsRes.data) ? brandsRes.data : [];
      console.log(`Retailer data received: list=${listData.length}, brands=${brandsData.length}`);

      const combined = [...listData, ...brandsData].map(r => {
        const name = r.name || r.username || (typeof r === 'string' ? r : '');
        return {
          name: name,
          _id: r._id || `test-${name}-${Math.random()}`
        };
      }).filter(r => r.name);

      // Always add common ones for fallback if none found
      const defaultRetailers = [
        { name: 'Rusty', _id: 'def-1' },
        { name: 'Billabong', _id: 'def-2' },
        { name: 'Quiksilver', _id: 'def-3' },
        { name: 'Rip Curl', _id: 'def-4' },
        { name: 'Surfstitch', _id: 'def-5' }
      ];

      const unique = Array.from(new Map([...defaultRetailers, ...combined].map(r => [r.name.toLowerCase(), r])).values());
      console.log(`Setting allRetailers with ${unique.length} items`);
      setAllRetailers(unique);
    } catch (e) { console.error('Error fetching all retailers:', e); }
  };

  const handleUpload = async (slotIndex, desktopFile, mobileFile, link, type = 'hero', target = null) => {
    if (!desktopFile && !mobileFile) return alert('Please select at least one image (Desktop or Mobile)');
    const formData = new FormData();
    formData.append('order', slotIndex);
    formData.append('linkUrl', link || '/');
    if (desktopFile) formData.append('banner', desktopFile);
    if (mobileFile) formData.append('mobileBanner', mobileFile);
    formData.append('type', type);
    if (target) formData.append('target', target);

    try {
      const res = await api.post('/api/promotions', formData);
      if (res.status === 201) {
        fetchPromotions();
      }
    } catch (e) { 
      const err = e.response?.data;
      alert('Upload failed: ' + (err?.details || err?.error || 'Server error'));
    }
  };

  const handlePartialUpload = async (promoId, desktopFile, mobileFile) => {
    const formData = new FormData();
    if (desktopFile) formData.append('banner', desktopFile);
    if (mobileFile) formData.append('mobileBanner', mobileFile);

    try {
      await api.patch(`/api/promotions/${promoId}`, formData);
      fetchPromotions();
    } catch (e) {
      alert('Update failed');
    }
  };

  const handleAddKey = async (e) => {
    e.preventDefault();
    const provider = e.target.provider.value;
    const apiKey = e.target.apiKey.value;
    try {
      await api.post('/api/integrations/keys', { provider, apiKey });
      fetchApiKeys();
      fetchSyncLogs();
      e.target.reset();
    } catch (e) { console.error('ADD_KEY_ERROR:', e); }
  };

  const handleImport = async (brandName) => {
    if (!brandName) return alert('Please enter a brand name');
    try {
      setIsSyncing(true);
      const res = await api.post('/api/integrations/ingest-simulate', { brandName, provider: 'COMMISSION_FACTORY' });
      alert(res.data.message || 'Brand data imported successfully');
      fetchBrands();
      fetchSyncLogs();
    } catch (e) { 
      alert('Import failed. Check system logs.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTestKey = async (id) => {
    try {
      const res = await api.post(`/api/integrations/keys/${id}/test`);
      alert(res.data.message);
      fetchApiKeys();
    } catch (e) { alert('Test failed'); }
  };

  const handlePartialDelete = async (promoId, type) => {
    try {
      await api.delete(`/api/promotions/${promoId}/${type}`);
      fetchPromotions();
    } catch (e) {
      alert('Delete failed');
    }
  };

  const handleManualSync = async (id) => {
    try {
      setIsSyncing(true);
      await api.post(`/api/integrations/keys/${id}/test`); // Re-using test for now as sync trigger
      alert('Sync completed successfully');
      fetchSyncLogs();
    } catch (e) { alert('Sync failed'); }
    finally { setIsSyncing(false); }
  };

  const handleMarketDiscovery = async () => {
    try {
      setIsDiscovering(true);
      await fetchDiscovery();
      alert('Discovery protocol completed');
    } catch (e) { alert('Discovery failed'); }
    finally { setIsDiscovering(false); }
  };

  const handleSnippetImport = async (idx, htmlString, viewType, targetElement) => {
    if (!htmlString) return;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const img = doc.querySelector('img') || doc.querySelector('IMG');

      if (!img) return alert('INVALID_SNIPPET: Image asset not found.');

      const imageUrl = img.getAttribute('src');

      if (!imageUrl) return alert('INVALID_SNIPPET: Missing image URL.');

      const formData = new FormData();
      formData.append('order', idx);
      formData.append('type', 'hero');
      
      // We no longer take the link from the snippet as the user manages it via the dropdown.
      
      if (viewType === 'desktop') {
        formData.append('imageUrl', imageUrl);
      } else {
        formData.append('mobileImageUrl', imageUrl);
      }

      await api.post('/api/promotions', formData);
      if (targetElement) targetElement.value = '';
      fetchPromotions();
      alert(`SNIPPET_INGESTED // ${viewType.toUpperCase()}_ASSET_LIVE`);
    } catch (e) {
      alert('IMPORT_FAILED');
    }
  };

  const handleAuthorizeBrand = async (brandName, provider) => {
    try {
      setIsSyncing(true);
      const res = await api.post('/api/integrations/ingest-simulate', { brandName, provider });
      alert(res.data.message);
      fetchBrands();
      fetchDiscovery();
    } catch (e) { alert('Authorization failed'); }
    finally { setIsSyncing(false); }
  };

  const handleOffboardBrand = async (brandId) => {
    if (!window.confirm('Confirm offboarding? ALL products for this brand will be deactivated.')) return;
    try {
      await api.patch(`/api/admin/retailers/brands/${brandId}`, { status: 'inactive' });
      fetchBrands();
    } catch (e) { alert('Offboard failed'); }
  };

  const handleUpdateOrchestration = async (brandId, page, active) => {
    try {
      const brand = activeBrands.find(b => b._id === brandId);
      if (!brand) return;
      let featuredIn = brand.featuredIn || [];
      if (active) {
        if (!featuredIn.includes(page)) featuredIn.push(page);
      } else {
        featuredIn = featuredIn.filter(p => p !== page);
      }
      await api.patch(`/api/admin/retailers/brands/${brandId}`, { featuredIn });
      fetchBrands();
    } catch (e) { alert('Update failed'); }
  };

  const handleExecuteCollisionScan = async () => {
    try {
      setIsSyncing(true);
      await fetchCollisions();
      alert('Scan complete. Collisions logged.');
    } catch (e) { alert('Scan failed'); }
    finally { setIsSyncing(false); }
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'analytics': {
        const ar = adminReport;
        const maxDailyClicks = ar?.dailyTrend?.length
          ? Math.max(...ar.dailyTrend.map(d => d.clicks), 1)
          : 1;
        const maxDailyImpressions = ar?.dailyTrend?.length
          ? Math.max(...ar.dailyTrend.map(d => d.impressions), 1)
          : 1;
        const maxMerchantCount = ar?.clicksByMerchant?.[0]?.count || 1;
        const maxBrandCount = ar?.clicksByBrand?.[0]?.count || 1;
        const maxCatCount = ar?.clicksByCategory?.[0]?.count || 1;

        const statCard = (label, value, accent) => (
          <div style={{ padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: '8px', opacity: 0.5, marginBottom: '6px', fontFamily: 'monospace', letterSpacing: '1px' }}>{label}</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: accent || '#fff' }}>{value}</div>
          </div>
        );

        const barRow = (label, value, max, suffix = '') => {
          const pct = max > 0 ? Math.max((value / max) * 100, 2) : 2;
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <div style={{ width: '130px', fontSize: '10px', fontWeight: '700', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label || '—'}</div>
              <div style={{ flex: 1, height: '6px', background: '#111', position: 'relative' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--color-savings)', transition: 'width 0.4s ease' }} />
              </div>
              <div style={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--color-savings)', minWidth: '60px', textAlign: 'right' }}>{value}{suffix}</div>
            </div>
          );
        };

        const rangeBtn = (r, label) => (
          <button
            key={r}
            onClick={() => { setAnalyticsRange(r); fetchAdminReport(r); }}
            style={{
              padding: '6px 14px', fontSize: '9px', fontWeight: '900', letterSpacing: '1px', cursor: 'pointer',
              background: analyticsRange === r ? '#fff' : 'transparent',
              color: analyticsRange === r ? '#000' : '#555',
              border: `1px solid ${analyticsRange === r ? '#fff' : '#222'}`,
              fontFamily: 'monospace'
            }}
          >{label}</button>
        );

        const subTabBtn = (id, label, icon) => (
          <button
            key={id}
            onClick={() => setAnalyticsSubTab(id)}
            style={{
              padding: '8px 18px', fontSize: '10px', fontWeight: '800', letterSpacing: '1px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              background: analyticsSubTab === id ? '#111' : 'transparent',
              color: analyticsSubTab === id ? '#fff' : '#555',
              border: 'none', borderBottom: analyticsSubTab === id ? '2px solid var(--color-savings)' : '2px solid transparent',
              fontFamily: 'monospace'
            }}
          >{icon} {label}</button>
        );

        return (
          <div className="profile-section animate-slide-up" style={{ background: '#000', padding: '0', border: '1px solid #1a1a1a' }}>

            {/* ── Header ── */}
            <div style={{ padding: '28px 30px', borderBottom: '1px solid #1a1a1a', background: 'linear-gradient(to bottom, #050505, #000)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h2 style={{ fontSize: '22px', letterSpacing: '3px', fontWeight: '900', color: '#fff', marginBottom: '4px' }}>FEED_ANALYTICS // PERFORMANCE_MONITOR</h2>
                  <div style={{ display: 'flex', gap: '20px', fontSize: '9px', color: '#555', fontFamily: 'monospace' }}>
                    <span>CURRENCY: AUD</span>
                    <span>SCHEMA: v2</span>
                    <span style={{ color: analyticsLoading ? 'var(--color-savings)' : '#333' }}>{analyticsLoading ? '◉ LOADING...' : '◎ READY'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {rangeBtn('7d', '7D')}
                  {rangeBtn('30d', '30D')}
                  {rangeBtn('90d', '90D')}
                  {rangeBtn('all', 'ALL')}
                  <button
                    className="brutalist-btn-outline"
                    style={{ fontSize: '9px', marginLeft: '10px' }}
                    onClick={() => fetchAdminReport(analyticsRange)}
                  >
                    <FaSyncAlt style={{ marginRight: '6px' }} /> REFRESH
                  </button>
                </div>
              </div>
            </div>

            {/* ── Sub-tab nav ── */}
            <div style={{ display: 'flex', borderBottom: '1px solid #111', padding: '0 30px', background: '#050505' }}>
              {subTabBtn('overview', 'OVERVIEW', <FaChartLine style={{ fontSize: '9px' }} />)}
              {subTabBtn('products', 'TOP PRODUCTS', <FaBullseye style={{ fontSize: '9px' }} />)}
              {subTabBtn('sections', 'FEED SECTIONS', <FaLayerGroup style={{ fontSize: '9px' }} />)}
              {subTabBtn('brands', 'BRANDS & CATEGORIES', <FaDatabase style={{ fontSize: '9px' }} />)}
            </div>

            <div style={{ padding: '30px' }}>

              {/* ── OVERVIEW sub-tab ── */}
              {analyticsSubTab === 'overview' && (
                <>
                  {/* KPI row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    {statCard('IMPRESSIONS', ar?.totalImpressions ?? '—', '#fff')}
                    {statCard('CARD_TAPS', ar?.totalCardTaps ?? '—', '#fff')}
                    {statCard('PDP_VIEWS', ar?.totalPdpViews ?? '—', '#fff')}
                    {statCard('OUTBOUND_CLICKS', ar?.totalClicks ?? '—', 'var(--color-savings)')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '32px' }}>
                    {statCard('CARD_TAP_RATE', ar?.totalImpressions > 0 ? `${ar.cardTapRate}%` : '—', '#fff')}
                    {statCard('PDP_FROM_TAP', ar?.totalCardTaps > 0 ? `${ar.pdpViewRate}%` : '—', '#fff')}
                    {statCard('DEAL_FROM_PDP', ar?.totalPdpViews > 0 ? `${ar.dealClickRate}%` : '—', '#fff')}
                    {statCard('IMPR→DEAL CTR', ar?.totalImpressions > 0 ? `${ar.overallCTR}%` : '—', 'var(--color-savings)')}
                  </div>

                  {/* Daily trend chart */}
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '10px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'monospace' }}>
                      <FaChartLine /> DAILY_TREND // CLICKS + IMPRESSIONS
                    </h3>
                    {ar?.dailyTrend?.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px', minWidth: `${ar.dailyTrend.length * 22}px` }}>
                          {ar.dailyTrend.map((d, i) => (
                            <div key={d.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flex: '0 0 18px' }} title={`${d.date}: ${d.clicks} clicks, ${d.impressions} impr.`}>
                              <div style={{ width: '8px', background: '#333', height: `${Math.round((d.impressions / maxDailyImpressions) * 70)}px`, minHeight: '2px' }} />
                              <div style={{ width: '8px', background: 'var(--color-savings)', height: `${Math.round((d.clicks / maxDailyClicks) * 70)}px`, minHeight: '2px' }} />
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', color: '#555', fontFamily: 'monospace' }}>
                            <div style={{ width: '10px', height: '10px', background: 'var(--color-savings)' }} /> CLICKS
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', color: '#555', fontFamily: 'monospace' }}>
                            <div style={{ width: '10px', height: '10px', background: '#333' }} /> IMPRESSIONS
                          </div>
                          <div style={{ fontSize: '9px', color: '#333', fontFamily: 'monospace', marginLeft: 'auto' }}>
                            {ar.dailyTrend[0]?.date} → {ar.dailyTrend[ar.dailyTrend.length - 1]?.date}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p style={{ opacity: 0.3, fontSize: '10px', fontFamily: 'monospace' }}>AWAITING_SIGNAL — no trend data yet</p>
                    )}
                  </div>

                  {/* Merchant + Category side-by-side */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                      <h3 style={{ fontSize: '10px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'monospace' }}>
                        <FaMousePointer /> TOP_RETAILERS // BY_CLICKS
                      </h3>
                      {ar?.clicksByMerchant?.slice(0, 8).map(m => barRow(m._id || 'Unknown', m.count, maxMerchantCount, ' clicks'))}
                      {!ar?.clicksByMerchant?.length && <p style={{ opacity: 0.3, fontSize: '10px', fontFamily: 'monospace' }}>AWAITING_SIGNAL...</p>}
                    </div>
                    <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                      <h3 style={{ fontSize: '10px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'monospace' }}>
                        <FaLayerGroup /> CATEGORY_HEATMAP
                      </h3>
                      {ar?.clicksByCategory?.slice(0, 8).map(c => barRow(c._id?.toUpperCase() || 'OTHER', c.count, maxCatCount, ' clicks'))}
                      {!ar?.clicksByCategory?.length && <p style={{ opacity: 0.3, fontSize: '10px', fontFamily: 'monospace' }}>AWAITING_SIGNAL...</p>}
                    </div>
                  </div>
                </>
              )}

              {/* ── TOP PRODUCTS sub-tab ── */}
              {analyticsSubTab === 'products' && (
                <div style={{ background: '#050505', border: '1px solid #111' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#0a0a0a' }}>
                        {['#', 'PRODUCT', 'MERCHANT', 'CATEGORY', 'PRICE', 'IMPRESSIONS', 'CLICKS', 'CTR'].map(h => (
                          <th key={h} style={{ padding: '12px 14px', fontSize: '9px', color: '#444', fontWeight: '800', letterSpacing: '1px', textAlign: 'left', fontFamily: 'monospace', borderBottom: '1px solid #111' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ar?.topProducts?.map((p, i) => {
                        const ctr = p.impressions > 0 ? ((p.clicks / p.impressions) * 100).toFixed(1) : '—';
                        return (
                          <tr key={String(p._id)} style={{ borderBottom: '1px solid #0d0d0d' }}>
                            <td style={{ padding: '12px 14px', fontSize: '10px', color: '#444', fontFamily: 'monospace' }}>{i + 1}</td>
                            <td style={{ padding: '12px 14px', maxWidth: '220px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '800', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                              <div style={{ fontSize: '9px', color: '#444', fontFamily: 'monospace' }}>{p.brand}</div>
                            </td>
                            <td style={{ padding: '12px 14px', fontSize: '10px', color: '#888' }}>{p.merchantName}</td>
                            <td style={{ padding: '12px 14px', fontSize: '10px', color: '#888' }}>{p.category}</td>
                            <td style={{ padding: '12px 14px', fontSize: '10px', color: '#ccc', fontFamily: 'monospace' }}>{p.price ? `$${p.price}` : '—'}</td>
                            <td style={{ padding: '12px 14px', fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{p.impressions}</td>
                            <td style={{ padding: '12px 14px', fontSize: '12px', fontWeight: '900', color: 'var(--color-savings)', fontFamily: 'monospace' }}>{p.clicks}</td>
                            <td style={{ padding: '12px 14px', fontSize: '10px', color: ctr !== '—' && parseFloat(ctr) > 5 ? 'var(--color-savings)' : '#888', fontFamily: 'monospace' }}>{ctr !== '—' ? `${ctr}%` : '—'}</td>
                          </tr>
                        );
                      })}
                      {!ar?.topProducts?.length && (
                        <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', fontSize: '10px', color: '#333', fontFamily: 'monospace' }}>AWAITING_SIGNAL — no click data yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── FEED SECTIONS sub-tab ── */}
              {analyticsSubTab === 'sections' && (
                <div>
                  <div style={{ background: '#050505', border: '1px solid #111', marginBottom: '20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#0a0a0a' }}>
                          {['FEED_SECTION', 'IMPRESSIONS', 'CARD_TAPS', 'CLICKS', 'TAP_RATE', 'CTR', 'SIGNAL_BAR'].map(h => (
                            <th key={h} style={{ padding: '12px 16px', fontSize: '9px', color: '#444', fontWeight: '800', letterSpacing: '1px', textAlign: 'left', fontFamily: 'monospace', borderBottom: '1px solid #111' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ar?.feedSectionPerformance?.map((s, i) => {
                          const maxClicks = ar.feedSectionPerformance[0]?.clicks || 1;
                          const pct = Math.max((s.clicks / maxClicks) * 100, 2);
                          return (
                            <tr key={s.section} style={{ borderBottom: '1px solid #0d0d0d' }}>
                              <td style={{ padding: '14px 16px', fontSize: '11px', fontWeight: '800', color: '#fff', fontFamily: 'monospace', textTransform: 'uppercase' }}>{s.section}</td>
                              <td style={{ padding: '14px 16px', fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{s.impressions.toLocaleString()}</td>
                              <td style={{ padding: '14px 16px', fontSize: '10px', color: '#fff', fontFamily: 'monospace' }}>{(s.cardTaps || 0).toLocaleString()}</td>
                              <td style={{ padding: '14px 16px', fontSize: '12px', fontWeight: '900', color: 'var(--color-savings)', fontFamily: 'monospace' }}>{s.clicks.toLocaleString()}</td>
                              <td style={{ padding: '14px 16px', fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{s.cardTapRate || '0.00'}%</td>
                              <td style={{ padding: '14px 16px', fontSize: '10px', color: parseFloat(s.ctr) > 5 ? 'var(--color-savings)' : '#888', fontFamily: 'monospace' }}>{s.ctr}%</td>
                              <td style={{ padding: '14px 16px', width: '200px' }}>
                                <div style={{ height: '6px', background: '#111' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: 'var(--color-savings)' }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {!ar?.feedSectionPerformance?.length && (
                          <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', fontSize: '10px', color: '#333', fontFamily: 'monospace' }}>AWAITING_SIGNAL — no section data yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                    <p style={{ fontSize: '10px', color: '#444', fontFamily: 'monospace', lineHeight: '1.8' }}>
                      SECTION_TRACKING: Pass <code style={{ background: '#111', padding: '1px 5px', color: 'var(--color-savings)' }}>feedSection</code> prop to ProductCard to attribute impressions and clicks to specific feed sections (e.g. "trending", "featured", "market_feed", "discovery").
                    </p>
                  </div>
                </div>
              )}

              {/* ── BRANDS & CATEGORIES sub-tab ── */}
              {analyticsSubTab === 'brands' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                    <h3 style={{ fontSize: '10px', fontWeight: '800', color: '#666', marginBottom: '20px', fontFamily: 'monospace' }}>
                      TOP_BRANDS // BY_CLICKS
                    </h3>
                    {ar?.clicksByBrand?.map(b => barRow(b._id || 'Unknown', b.count, maxBrandCount, ' clicks'))}
                    {!ar?.clicksByBrand?.length && <p style={{ opacity: 0.3, fontSize: '10px', fontFamily: 'monospace' }}>AWAITING_SIGNAL...</p>}
                  </div>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                    <h3 style={{ fontSize: '10px', fontWeight: '800', color: '#666', marginBottom: '20px', fontFamily: 'monospace' }}>
                      TOP_CATEGORIES // BY_CLICKS
                    </h3>
                    {ar?.clicksByCategory?.map(c => barRow(c._id?.toUpperCase() || 'OTHER', c.count, maxCatCount, ' clicks'))}
                    {!ar?.clicksByCategory?.length && <p style={{ opacity: 0.3, fontSize: '10px', fontFamily: 'monospace' }}>AWAITING_SIGNAL...</p>}
                  </div>
                </div>
              )}

            </div>
          </div>
        );
      }

      case 'retailer-hub':
        return (
          <div className="profile-section animate-slide-up" style={{ background: '#000', padding: '0', border: '1px solid #1a1a1a' }}>

            {/* ── RETAILER HUB HEADER ── */}
            <div style={{ padding: '30px', borderBottom: '1px solid #1a1a1a', background: 'linear-gradient(to bottom, #050505, #000)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '24px', letterSpacing: '2px', fontWeight: '900', color: '#fff', marginBottom: '5px' }}>RETAILER_HUB // INTEGRATION_CONTROL</h2>
                  <div style={{ display: 'flex', gap: '24px', fontSize: '10px', color: '#666', fontFamily: 'monospace' }}>
                    <span>ACTIVE_NODES: {apiKeys.length}</span>
                    <span>LIVE_BRANDS: {activeBrands.filter(b => b.status === 'active').length}</span>
                    <span>LAST_HEARTBEAT: {new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '2px', background: '#0a0a0a', border: '1px solid #1a1a1a', padding: '4px' }}>
                  {[
                    { id: 'overview', label: 'OVERVIEW' },
                    { id: 'integrations', label: 'INTEGRATIONS' },
                    { id: 'affiliate', label: 'AFFILIATE' },
                    { id: 'activity', label: 'ACTIVITY' }
                  ].map(sub => (
                    <button key={sub.id}
                      onClick={() => setRetailerHubSubTab(sub.id)}
                      style={{
                        fontSize: '10px', padding: '8px 18px', cursor: 'pointer', border: 'none', letterSpacing: '1px', fontWeight: '800', fontFamily: 'monospace',
                        background: retailerHubSubTab === sub.id ? 'var(--color-savings)' : 'transparent',
                        color: retailerHubSubTab === sub.id ? '#000' : '#555',
                        transition: 'all 0.15s ease'
                      }}
                    >{sub.label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── OVERVIEW SUB-TAB ── */}
            {retailerHubSubTab === 'overview' && (
              <div style={{ padding: '30px' }}>

                {/* Quick Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '40px' }}>
                  {[
                    { label: 'ACTIVE_INTEGRATIONS', value: apiKeys.length, color: 'var(--color-savings)' },
                    { label: 'LIVE_BRANDS', value: activeBrands.filter(b => b.status === 'active').length, color: '#fff' },
                    { label: 'TOTAL_RETAILERS', value: allRetailers.length, color: '#fff' },
                    { label: 'OUTBOUND_CLICKS', value: adminReport?.totalClicks || 0, color: 'var(--color-savings)' }
                  ].map(stat => (
                    <div key={stat.label} className="retailer-card" style={{ margin: '0', padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a' }}>
                      <div style={{ fontSize: '8px', opacity: 0.5, marginBottom: '5px', letterSpacing: '1px' }}>{stat.label}</div>
                      <div style={{ fontSize: '24px', fontWeight: '900', color: stat.color, fontFamily: 'monospace' }}>{stat.value}</div>
                    </div>
                  ))}
                </div>

                {/* Retailer Registry */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaLayerGroup style={{ color: 'var(--color-savings)' }} /> RETAILER_REGISTRY // ACTIVE_NODES
                  </h3>
                  <div style={{ background: '#050505', border: '1px solid #111' }}>
                    <table className="brand-table" style={{ border: 'none' }}>
                      <thead style={{ background: '#0a0a0a' }}>
                        <tr>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>RETAILER_NAME</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>SOURCE_TYPE</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>STATUS</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>PLACEMENT</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeBrands.length === 0 ? (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '60px', opacity: 0.2, fontSize: '11px', fontFamily: 'monospace' }}>
                              NO_RETAILERS_DETECTED... RUN_DISCOVERY_TO_POPULATE
                            </td>
                          </tr>
                        ) : (
                          activeBrands.map(brand => (
                            <tr key={brand._id} style={{ borderBottom: '1px solid #0f0f0f' }}>
                              <td style={{ fontWeight: '900', color: '#fff', letterSpacing: '0.5px' }}>{brand.name?.toUpperCase()}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#666' }}>
                                {brand.source || (brand.isPartnered ? 'RAKUTEN' : 'INTERNAL')}
                              </td>
                              <td>
                                <span style={{
                                  fontSize: '9px', padding: '3px 8px',
                                  background: brand.status === 'active' ? 'rgba(0,230,118,0.1)' : 'rgba(255,82,82,0.1)',
                                  color: brand.status === 'active' ? 'var(--color-savings)' : '#ff5252',
                                  border: `1px solid ${brand.status === 'active' ? 'var(--color-savings)' : '#ff5252'}`
                                }}>
                                  {brand.status === 'active' ? 'LIVE' : 'OFFLINE'}
                                </span>
                              </td>
                              <td style={{ fontSize: '9px', fontFamily: 'monospace', color: '#555' }}>
                                {brand.featuredIn?.length > 0 ? brand.featuredIn.map(f => f.toUpperCase()).join(', ') : 'UNPLACED'}
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button className="brutalist-btn-outline" style={{ fontSize: '9px', padding: '4px 10px' }}
                                    onClick={() => { setIngestionSubTab('orchestrator'); setSelectedTab('ingestion-engine'); }}>
                                    PLACE
                                  </button>
                                  {brand.status === 'active' ? (
                                    <button className="reject-button" style={{ fontSize: '9px', padding: '4px 8px' }} onClick={() => handleOffboardBrand(brand._id)}>OFFBOARD</button>
                                  ) : (
                                    <button className="refresh-button" style={{ fontSize: '9px', padding: '4px 8px' }} onClick={() => handleAuthorizeBrand(brand.name, brand.source || 'INTERNAL')}>RESTORE</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sync Health */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaWifi style={{ color: 'var(--color-savings)' }} /> SYNC_HEALTH_MONITOR
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {apiKeys.length === 0 ? (
                        <p style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace' }}>NO_NODES_REGISTERED</p>
                      ) : apiKeys.slice(0, 4).map(k => (
                        <div key={k._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#000', border: '1px solid #111' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span className="pulse-dot" style={{ background: k.status === 'ERROR' ? '#ff5252' : 'var(--color-savings)', boxShadow: `0 0 8px ${k.status === 'ERROR' ? '#ff5252' : 'var(--color-savings)'}` }}></span>
                            <span style={{ fontSize: '11px', fontWeight: '900' }}>{k.provider}</span>
                          </div>
                          <span style={{ fontSize: '9px', color: k.status === 'ERROR' ? '#ff5252' : 'var(--color-savings)', fontFamily: 'monospace' }}>
                            {k.status === 'ERROR' ? 'NODE_OFFLINE' : 'NODE_ONLINE'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaBolt style={{ color: 'var(--color-savings)' }} /> TOP_AFFILIATE_SIGNALS
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {adminReport?.clicksByMerchant?.slice(0, 4).map((m, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#000', border: '1px solid #111' }}>
                          <span style={{ fontSize: '11px', fontWeight: '900', color: '#fff' }}>{m._id}</span>
                          <span style={{ fontSize: '10px', color: 'var(--color-savings)', fontFamily: 'monospace', fontWeight: '900' }}>{m.count} CLICKS</span>
                        </div>
                      ))}
                      {!adminReport?.clicksByMerchant?.length && (
                        <p style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace' }}>AWAITING_TRAFFIC_DATA...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── INTEGRATIONS SUB-TAB ── */}
            {retailerHubSubTab === 'integrations' && (
              <div style={{ padding: '30px' }}>

                {/* Add Integration Form */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaBolt style={{ color: 'var(--color-savings)' }} /> INITIALIZE_NEW_PARTNER_PROTOCOL
                  </h3>
                  <form onSubmit={handleAddKey} style={{ display: 'grid', gridTemplateColumns: '250px 1fr 180px', gap: '1px', background: '#222', border: '1px solid #222' }}>
                    <input name="provider" placeholder="PARTNER_NAME (e.g. RAKUTEN)" style={{ background: '#000', border: 'none', height: '50px', padding: '0 20px', color: '#fff', fontSize: '12px' }} required />
                    <input name="apiKey" placeholder="PROTOCOL_ACCESS_KEY" style={{ background: '#000', border: 'none', height: '50px', padding: '0 20px', color: 'var(--color-savings)', fontFamily: 'monospace', fontSize: '12px' }} required />
                    <button className="brutalist-btn-dark" style={{ height: '50px', borderRadius: '0', fontSize: '11px', background: 'var(--color-savings)', color: '#000', border: 'none' }}>
                      AUTHORIZE_NODE_v1
                    </button>
                  </form>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '15px', fontSize: '9px', color: '#444', fontFamily: 'monospace' }}>
                    <span>SUPPORTED: RAKUTEN | SHOPIFY_API | WOCOMMERCE | IMPACT | COMMISSION_FACTORY | CSV_FEED</span>
                  </div>
                </div>

                {/* Active Integration Nodes */}
                <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaLayerGroup /> ACTIVE_INTEGRATION_MODULES
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                  {apiKeys?.length === 0 ? (
                    <div style={{ gridColumn: '1/-1', padding: '80px', textAlign: 'center', border: '1px dashed #222', background: '#050505' }}>
                      <FaNetworkWired style={{ fontSize: '40px', color: '#222', marginBottom: '20px' }} />
                      <p style={{ color: '#444', fontSize: '12px', fontFamily: 'monospace' }}>AWAITING_EXTERNAL_SIGNAL... NO_NODES_FOUND</p>
                    </div>
                  ) : (
                    apiKeys?.map(k => (
                      <div key={k._id} className="retailer-card" style={{ margin: '0', padding: '0', border: '1px solid #1a1a1a', background: '#050505', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '2px', background: 'linear-gradient(to right, transparent, var(--color-savings), transparent)', opacity: 0.3 }}></div>
                        <div style={{ padding: '20px', borderBottom: '1px solid #111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ width: '40px', height: '40px', background: '#111', border: '1px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-savings)' }}>
                              <FaMicrochip />
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '900', color: '#fff', letterSpacing: '1px' }}>{k.provider}</div>
                              <div style={{ fontSize: '9px', color: '#444', fontFamily: 'monospace' }}>UUID: {k._id.toUpperCase()}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span className="pulse-dot" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', background: k.status === 'ERROR' ? '#ff5252' : 'var(--color-savings)', boxShadow: k.status === 'ERROR' ? '0 0 10px #ff5252' : '0 0 10px var(--color-savings)' }}></span>
                            <span style={{ fontSize: '9px', color: k.status === 'ERROR' ? '#ff5252' : 'var(--color-savings)', fontWeight: 'bold', fontFamily: 'monospace' }}>
                              {k.status === 'ERROR' ? 'NODE_OFFLINE' : 'NODE_ONLINE'}
                            </span>
                          </div>
                        </div>
                        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                          <div style={{ padding: '15px', background: '#000', border: '1px solid #111' }}>
                            <div style={{ fontSize: '8px', color: '#444', marginBottom: '5px' }}>SYNC_LATENCY</div>
                            <div style={{ fontSize: '14px', fontWeight: '900', color: '#fff', fontFamily: 'monospace' }}>--ms</div>
                          </div>
                          <div style={{ padding: '15px', background: '#000', border: '1px solid #111' }}>
                            <div style={{ fontSize: '8px', color: '#444', marginBottom: '5px' }}>DATA_THROUGHPUT</div>
                            <div style={{ fontSize: '14px', fontWeight: '900', color: 'var(--color-savings)', fontFamily: 'monospace' }}>0.0 MB/s</div>
                          </div>
                        </div>
                        <div style={{ padding: '0 20px 20px 20px', display: 'flex', gap: '10px' }}>
                          <button className="brutalist-btn-dark" style={{ flex: 1, fontSize: '10px', height: '35px' }} onClick={() => handleManualSync(k._id)} disabled={isSyncing}>
                            <FaSyncAlt className={isSyncing ? 'fa-spin' : ''} /> {isSyncing ? 'SYNCING...' : 'EXECUTE_SYNC'}
                          </button>
                          <button className="brutalist-btn-outline" style={{ width: '80px', fontSize: '10px' }} onClick={() => handleTestKey(k._id)}>TEST</button>
                          <button className="reject-button" style={{ width: '40px', borderRadius: '0' }} onClick={() => {
                            if (window.confirm('TERMINATE_CONNECTION?')) api.delete(`/api/integrations/keys/${k._id}`).then(() => fetchApiKeys());
                          }}>
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Supported Integrations Roadmap */}
                <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSatellite style={{ color: 'var(--color-savings)' }} /> INTEGRATION_MODULES // CAPABILITY_MANIFEST
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {[
                      { name: 'RAKUTEN_AFFILIATE', status: 'LIVE', color: 'var(--color-savings)' },
                      { name: 'CSV_XML_FEED', status: 'LIVE', color: 'var(--color-savings)' },
                      { name: 'SCRAPER_ENGINE', status: 'LIVE', color: 'var(--color-savings)' },
                      { name: 'SHOPIFY_API', status: 'ROADMAP', color: '#2196f3' },
                      { name: 'WOOCOMMERCE', status: 'ROADMAP', color: '#2196f3' },
                      { name: 'DIRECT_RETAILER_API', status: 'ROADMAP', color: '#2196f3' },
                      { name: 'IMPACT.COM', status: 'PENDING', color: '#ff9800' },
                      { name: 'COMMISSION_FACTORY', status: 'PENDING', color: '#ff9800' },
                      { name: 'WEBHOOK_SYNC', status: 'ROADMAP', color: '#2196f3' }
                    ].map(m => (
                      <div key={m.name} style={{ padding: '12px 16px', background: '#000', border: `1px solid ${m.color}22`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', fontWeight: '800', fontFamily: 'monospace' }}>{m.name}</span>
                        <span style={{ fontSize: '8px', color: m.color, padding: '2px 6px', border: `1px solid ${m.color}` }}>{m.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── AFFILIATE SUB-TAB ── */}
            {retailerHubSubTab === 'affiliate' && (
              <div style={{ padding: '30px' }}>

                {/* Network Connectivity Status */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaHandshake style={{ color: 'var(--color-savings)' }} /> AFFILIATE_NETWORK_STATUS
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                    {[
                      { name: 'Impact.com', status: 'PENDING_APPROVAL', color: 'var(--color-savings)' },
                      { name: 'Commission Factory', status: 'NOT_APPLIED', color: '#ff5252' },
                      { name: 'Rakuten', status: 'ACTIVE', color: 'var(--color-savings)' }
                    ].map(network => (
                      <div key={network.name} style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
                          <div style={{ width: '35px', height: '35px', background: '#0a0a0a', border: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaLink style={{ color: network.color, fontSize: '14px' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '900', color: '#fff' }}>{network.name}</div>
                            <div style={{ fontSize: '9px', color: '#555', fontFamily: 'monospace' }}>AFFILIATE_NETWORK</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '9px', color: network.color, border: `1px solid ${network.color}`, padding: '3px 8px' }}>{network.status}</span>
                          <span style={{ fontSize: '9px', opacity: 0.4, fontFamily: 'monospace' }}>ID: --</span>
                        </div>
                        {network.status === 'ACTIVE' && (
                          <div style={{ marginTop: '12px', padding: '10px', background: '#000', border: '1px solid #111' }}>
                            <div style={{ fontSize: '8px', color: '#444', marginBottom: '4px' }}>COMMISSION_RATE</div>
                            <div style={{ fontSize: '13px', fontWeight: '900', color: 'var(--color-savings)' }}>3.0–8.5%</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Traffic Proof Data */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaChartLine style={{ color: 'var(--color-savings)' }} /> TRAFFIC_PROOF // TOP_PERFORMING_RETAILERS
                  </h3>
                  <div style={{ background: '#050505', border: '1px solid #111' }}>
                    <table className="brand-table" style={{ border: 'none' }}>
                      <thead style={{ background: '#0a0a0a' }}>
                        <tr>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>RETAILER</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>CLICKS_SENT</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>TOP_PRODUCT</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>COMMISSION_EST</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>VALUATION_EST</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminReport?.clicksByMerchant?.slice(0, 10).map((m, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #0f0f0f' }}>
                            <td style={{ fontWeight: '900', color: '#fff' }}>{m._id}</td>
                            <td style={{ fontFamily: 'monospace', color: 'var(--color-savings)', fontWeight: '900' }}>{m.count}</td>
                            <td style={{ fontSize: '10px', color: '#666' }}>
                              {adminReport?.topProducts?.find(tp => tp.merchant === m._id)?.name || '--'}
                            </td>
                            <td style={{ fontSize: '10px', color: '#2196f3', fontFamily: 'monospace' }}>${(m.count * 0.85).toFixed(2)}</td>
                            <td style={{ fontSize: '10px', color: '#fff', fontFamily: 'monospace' }}>${(m.count * 15.45).toFixed(2)}</td>
                          </tr>
                        ))}
                        {!adminReport?.clicksByMerchant?.length && (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '60px', opacity: 0.2, fontSize: '11px', fontFamily: 'monospace' }}>AWAITING_TRAFFIC_DATA...</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Commission & Onboarding Notes */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px' }}>COMMISSION_TRACKING</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'TOTAL_ESTIMATED_REVENUE', value: `$${((adminReport?.totalClicks || 0) * 15.45).toFixed(2)}`, color: 'var(--color-savings)' },
                        { label: 'ESTIMATED_COMMISSIONS', value: `$${((adminReport?.totalClicks || 0) * 0.85).toFixed(2)}`, color: '#2196f3' },
                        { label: 'TOTAL_OUTBOUND_CLICKS', value: adminReport?.totalClicks || 0, color: '#fff' }
                      ].map(item => (
                        <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#000', border: '1px solid #111' }}>
                          <span style={{ fontSize: '10px', color: '#666' }}>{item.label}</span>
                          <span style={{ fontSize: '13px', fontWeight: '900', color: item.color, fontFamily: 'monospace' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px' }}>ONBOARDING_PIPELINE</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'APPLY — Commission Factory', action: 'Apply to join the network', status: 'TODO' },
                        { label: 'APPLY — Impact.com', action: 'Application in progress', status: 'IN_PROGRESS' },
                        { label: 'EXPAND — Rakuten', action: 'Add more merchants', status: 'ACTIVE' }
                      ].map(item => (
                        <div key={item.label} style={{ padding: '12px', background: '#000', border: '1px solid #111' }}>
                          <div style={{ fontSize: '10px', fontWeight: '900', color: '#fff', marginBottom: '4px' }}>{item.label}</div>
                          <div style={{ fontSize: '9px', color: '#444' }}>{item.action}</div>
                          <div style={{ fontSize: '8px', color: item.status === 'ACTIVE' ? 'var(--color-savings)' : item.status === 'IN_PROGRESS' ? '#ff9800' : '#555', marginTop: '6px', fontFamily: 'monospace' }}>
                            [{item.status}]
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── ACTIVITY SUB-TAB ── */}
            {retailerHubSubTab === 'activity' && (
              <div style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaTerminal style={{ color: 'var(--color-savings)' }} /> LIVE_PROTOCOL_STREAM
                  </h3>
                  <button className="brutalist-btn-outline" style={{ fontSize: '9px', padding: '6px 14px' }} onClick={() => { fetchApiKeys(); fetchSyncLogs(); }}>
                    <FaSyncAlt style={{ marginRight: '6px' }} /> REFRESH_LOGS
                  </button>
                </div>

                {/* Encryption + System Status Banner */}
                <div style={{ display: 'flex', gap: '15px', marginBottom: '25px' }}>
                  {[
                    { label: 'ENCRYPTION', value: 'AES-256_ACTIVE', color: 'var(--color-savings)' },
                    { label: 'PROTOCOL', value: 'RAKUTEN_NATIVE_v2', color: '#2196f3' },
                    { label: 'SYSTEM_STATUS', value: 'OPERATIONAL', color: '#fff' },
                    { label: 'PORT_LISTEN', value: '5000_AUTH_SECURE', color: '#666' }
                  ].map(s => (
                    <div key={s.label} style={{ padding: '10px 16px', background: '#050505', border: '1px solid #111', flex: 1 }}>
                      <div style={{ fontSize: '8px', color: '#444', marginBottom: '4px' }}>{s.label}</div>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: s.color, fontFamily: 'monospace' }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* Live Log Terminal */}
                <div style={{ border: '1px solid #1a1a1a', background: '#050505' }}>
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid #111', background: '#0a0a0a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'monospace' }}>
                      <span style={{ color: 'var(--color-savings)' }}>●</span> SYNC_LOG_STREAM // LAST_50_EVENTS
                    </div>
                    <div style={{ fontSize: '9px', color: '#333', fontFamily: 'monospace' }}>{syncLogs.length} EVENTS_CAPTURED</div>
                  </div>
                  <div style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto', background: '#000', fontFamily: 'monospace' }}>
                    {syncLogs?.length === 0 ? (
                      <div style={{ color: '#222', fontSize: '11px' }}>_IDLE_SYSTEM_READY_FOR_DATA_STREAM...</div>
                    ) : (
                      syncLogs?.map((log, i) => (
                        <div key={i} style={{ fontSize: '11px', marginBottom: '8px', display: 'flex', gap: '20px', whiteSpace: 'nowrap', borderBottom: '1px solid #0a0a0a', paddingBottom: '6px' }}>
                          <span style={{ color: '#333', minWidth: '80px' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                          <span style={{ color: log.status === 'SUCCESS' ? 'var(--color-savings)' : '#ff5252', fontWeight: '900', width: '80px' }}>
                            {log.status === 'SUCCESS' ? '>>_OK' : '>>_ERR'}
                          </span>
                          <span style={{ color: '#666', textTransform: 'uppercase', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.details}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Import Logs - Brands */}
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaDatabase /> IMPORT_LOG // BRAND_INGESTION_HISTORY
                  </h3>
                  <div style={{ background: '#050505', border: '1px solid #111' }}>
                    <table className="brand-table" style={{ border: 'none' }}>
                      <thead style={{ background: '#0a0a0a' }}>
                        <tr>
                          <th style={{ fontSize: '9px', color: '#444' }}>BRAND_NODE</th>
                          <th style={{ fontSize: '9px', color: '#444' }}>SOURCE_PROTOCOL</th>
                          <th style={{ fontSize: '9px', color: '#444' }}>INGESTION_STATUS</th>
                          <th style={{ fontSize: '9px', color: '#444' }}>PAGE_PLACEMENT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeBrands.slice(0, 10).map(b => (
                          <tr key={b._id} style={{ borderBottom: '1px solid #0f0f0f' }}>
                            <td style={{ fontWeight: '900', color: '#fff' }}>{b.name?.toUpperCase()}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#555' }}>{b.source || 'INTERNAL'}</td>
                            <td>
                              <span style={{ fontSize: '9px', color: b.status === 'active' ? 'var(--color-savings)' : '#ff5252', fontFamily: 'monospace' }}>
                                {b.status === 'active' ? 'LIVE' : 'OFFLINE'}
                              </span>
                            </td>
                            <td style={{ fontSize: '9px', color: '#555', fontFamily: 'monospace' }}>
                              {b.featuredIn?.join(', ').toUpperCase() || 'UNPLACED'}
                            </td>
                          </tr>
                        ))}
                        {activeBrands.length === 0 && (
                          <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', opacity: 0.2, fontSize: '11px', fontFamily: 'monospace' }}>NO_IMPORT_HISTORY...</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

          </div>
        );

      case 'banners':
        return (
          <div className="profile-section animate-slide-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
              <h2>Banner Management</h2>
              <div className="tab-switcher" style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className={`brutalist-btn ${bannerSection === 'hero' ? 'brutalist-btn-dark' : 'brutalist-btn-outline'}`}
                  onClick={() => setBannerSection('hero')}
                  style={{ fontSize: '10px', padding: '8px 15px' }}
                >
                  HERO_SLIDER
                </button>
                <button 
                  className={`brutalist-btn ${bannerSection === 'category' ? 'brutalist-btn-dark' : 'brutalist-btn-outline'}`}
                  onClick={() => setBannerSection('category')}
                  style={{ fontSize: '10px', padding: '8px 15px' }}
                >
                  PARTNER_ASSETS
                </button>
              </div>
            </div>

            {bannerSection === 'hero' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <p style={{ opacity: 0.6, marginBottom: '10px', fontSize: '12px' }}>Independent asset management for high-performance sliders.</p>
                {[0, 1, 2, 3, 4, 5].map(idx => {
                  const promo = promotions.find(p => p.order === idx && p.type === 'hero');
                  return (
                    <div key={idx} className="retailer-card" style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 1.5fr', gap: '20px', alignItems: 'center', padding: '15px' }}>
                      <div style={{ fontWeight: '900', color: 'var(--color-savings)', fontSize: '18px' }}>{idx + 1}</div>
                      
                      {/* Desktop Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '800', opacity: 0.6 }}>DESKTOP_VIEW</span>
                        {promo?.imageUrl ? (
                          <div style={{ position: 'relative', width: '100%' }}>
                            <img src={promo.imageUrl} alt="" style={{ width: '100%', height: '60px', objectFit: 'cover', border: '1px solid #333' }} />
                            <button 
                              onClick={() => handlePartialDelete(promo._id, 'desktop')}
                              style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ff5252', border: 'none', color: 'white', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex' }}
                            >
                              <FaTrash size={10} />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label className="brutalist-btn brutalist-btn-outline" style={{ fontSize: '9px', padding: '10px', textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed' }}>
                              <FaPlus style={{ marginBottom: '4px' }} /><br/>UPLOAD
                              <input type="file" hidden onChange={(e) => {
                                if (!e.target.files[0]) return;
                                if (promo) handlePartialUpload(promo._id, e.target.files[0], null);
                                else handleUpload(idx, e.target.files[0], null, '/');
                              }} />
                            </label>
                            <input 
                              placeholder="Paste Snippet..."
                              className="retailers-search"
                              style={{ fontSize: '8px', padding: '5px', background: '#000', color: '#00E676' }}
                              onBlur={(e) => handleSnippetImport(idx, e.target.value, 'desktop', e.target)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Mobile Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '800', opacity: 0.6 }}>MOBILE_VIEW</span>
                        {promo?.mobileImageUrl ? (
                          <div style={{ position: 'relative', width: '60px' }}>
                            <img src={promo.mobileImageUrl} alt="" style={{ width: '100%', height: '60px', objectFit: 'cover', border: '1px solid #333' }} />
                            <button 
                              onClick={() => handlePartialDelete(promo._id, 'mobile')}
                              style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ff5252', border: 'none', color: 'white', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex' }}
                            >
                              <FaTrash size={10} />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label className="brutalist-btn brutalist-btn-outline" style={{ width: '60px', height: '40px', fontSize: '8px', padding: '0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderStyle: 'dashed' }}>
                              <FaPlus /><br/>UPLOAD
                              <input type="file" hidden onChange={(e) => {
                                if (!e.target.files[0]) return;
                                if (promo) handlePartialUpload(promo._id, null, e.target.files[0]);
                                else handleUpload(idx, null, e.target.files[0], '/');
                              }} />
                            </label>
                            <input 
                              placeholder="Snippet..."
                              className="retailers-search"
                              style={{ width: '60px', fontSize: '7px', padding: '3px', background: '#000', color: '#00E676' }}
                              onBlur={(e) => handleSnippetImport(idx, e.target.value, 'mobile', e.target)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Config Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '800', opacity: 0.6 }}>LINK_TO_RETAILER_FEED</span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <select 
                              className="retailers-search" 
                              style={{ flex: 1, padding: '8px', fontSize: '11px', background: '#000', color: '#fff', border: '1px solid #222', cursor: 'pointer' }}
                              value={promo?.linkUrl?.includes('retailers=') ? decodeURIComponent(promo.linkUrl.split('retailers=')[1].split('&')[0]) : ''}
                              onChange={async (e) => {
                                if (!promo) return alert('Please upload an image for this slot first.');
                                const retailerName = e.target.value;
                                const linkUrl = retailerName ? `/all-brands?retailers=${encodeURIComponent(retailerName)}` : '/';
                                const formData = new FormData();
                                formData.append('linkUrl', linkUrl);
                                await api.patch(`/api/promotions/${promo._id}`, formData);
                                fetchPromotions();
                              }}
                            >
                              <option value="">SELECT_RETAILER_REDIRECT</option>
                              {siteRetailers.map(retailer => (
                                <option key={retailer} value={retailer}>{retailer.toUpperCase()}</option>
                              ))}
                            </select>
                            {promo && (
                              <button className="reject-button" style={{ padding: '8px' }} onClick={() => {
                                if(window.confirm('TERMINATE_SLOT?')) api.delete(`/api/promotions/${promo._id}`).then(() => fetchPromotions());
                              }}>
                                <FaTrash />
                              </button>
                            )}
                          </div>
                          <div style={{ fontSize: '9px', color: '#666', marginTop: '5px' }}>
                            Current Link: <span style={{ color: 'var(--color-savings)' }}>{promo?.linkUrl || '/'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '10px' }}>
                  <p style={{ opacity: 0.6, fontSize: '12px', margin: 0 }}>Syncing high-fidelity hero assets from partnered Rakuten networks.</p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="brutalist-btn-dark" style={{ fontSize: '10px', background: '#fff', color: '#000' }} onClick={async () => {
                      try {
                        const response = await api.get('/api/admin/retailers/download-partner-banners', { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'partner-banners.zip');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (e) { alert('DOWNLOAD_FAILED'); }
                    }}>
                      <FaDownload style={{ marginRight: '8px' }} /> DOWNLOAD_ALL_ASSETS (.ZIP)
                    </button>
                    <button className="brutalist-btn-outline" style={{ fontSize: '10px' }} onClick={async () => {
                      setIsSyncing(true);
                      try {
                        await api.post('/api/admin/retailers/scrape-partner-banners');
                        alert('BANNER_SCRAPE_PROTOCOL_COMPLETE');
                        fetchBrands();
                      } catch (e) { alert('SCRAPE_FAILED'); }
                      finally { setIsSyncing(false); }
                    }}>
                      <FaSyncAlt className={isSyncing ? 'fa-spin' : ''} style={{ marginRight: '8px' }} /> EXECUTE_GLOBAL_SCRAPE
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
                  {activeBrands.filter(b => b.source === 'RAKUTEN' || b.isPartnered).map((brand, idx) => {
                    const banner = brand.banners?.[0]?.url;
                    return (
                      <div key={idx} className="retailer-card" style={{ margin: 0, padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: '900', fontSize: '14px', letterSpacing: '1px' }}>{brand.name.toUpperCase()}</div>
                          <div style={{ fontSize: '8px', color: 'var(--color-savings)', fontFamily: 'monospace', padding: '2px 6px', border: '1px solid var(--color-savings)' }}>RAKUTEN_PARTNER</div>
                        </div>
                        
                        <div style={{ width: '100%', height: '180px', background: '#000', border: '1px solid #222', overflow: 'hidden', position: 'relative' }}>
                          {banner ? (
                            <>
                              <img src={banner} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
                              <div style={{ position: 'absolute', bottom: '0', width: '100%', padding: '10px', background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)', fontSize: '9px', fontFamily: 'monospace', color: '#666' }}>
                                ASSET_URL: {banner.substring(0, 40)}...
                              </div>
                            </>
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '10px', fontFamily: 'monospace' }}>
                              NO_ASSET_DETECTED
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button 
                            className="brutalist-btn-dark" 
                            style={{ flex: 1, fontSize: '10px', background: banner ? 'var(--color-savings)' : '#222', color: '#000', border: 'none' }}
                            onClick={async () => {
                              if (!banner) return alert('No asset to download');
                              const filename = `${brand.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
                              window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/admin/retailers/proxy-download?url=${encodeURIComponent(banner)}&filename=${filename}`, '_blank');
                            }}
                          >
                            <FaDownload style={{ marginRight: '8px' }} /> DOWNLOAD_ASSET
                          </button>
                          <button 
                            className="brutalist-btn-outline" 
                            style={{ fontSize: '10px' }}
                            onClick={() => {
                              if (!banner) return;
                              navigator.clipboard.writeText(banner);
                              alert('ASSET_URL_COPIED');
                            }}
                          >
                            COPY_URL
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {activeBrands.filter(b => b.source === 'RAKUTEN' || b.isPartnered).length === 0 && (
                    <div style={{ gridColumn: '1/-1', padding: '60px', textAlign: 'center', border: '1px dashed #222', background: '#050505', color: '#444', fontFamily: 'monospace', fontSize: '12px' }}>
                      AWAITING_PARTNER_SIGNALS... NO_RAKUTEN_NODES_ACTIVE
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        );

      case 'ingestion-engine':
        return (
          <div className="profile-section animate-slide-up" style={{ background: '#000', padding: '0', border: '1px solid #1a1a1a' }}>

            {/* ── INGESTION ENGINE HEADER ── */}
            <div style={{ padding: '30px', borderBottom: '1px solid #1a1a1a', background: 'linear-gradient(to bottom, #050505, #000)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '24px', letterSpacing: '2px', fontWeight: '900', color: '#fff', marginBottom: '5px' }}>INGESTION_ENGINE // DATA_PIPELINE</h2>
                  <div style={{ display: 'flex', gap: '24px', fontSize: '10px', color: '#666', fontFamily: 'monospace' }}>
                    <span>PIPELINE_STATUS: OPERATIONAL</span>
                    <span>LIVE_PRODUCTS: {reports?.totalProducts || 0}</span>
                    <span>BRANDS_AUTHORISED: {activeBrands.filter(b => b.status === 'active').length}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '2px', background: '#0a0a0a', border: '1px solid #1a1a1a', padding: '4px' }}>
                  {[
                    { id: 'pipeline', label: 'PIPELINE' },
                    { id: 'normalizer', label: 'NORMALIZER' },
                    { id: 'orchestrator', label: 'ORCHESTRATOR' }
                  ].map(sub => (
                    <button key={sub.id}
                      onClick={() => setIngestionSubTab(sub.id)}
                      style={{
                        fontSize: '10px', padding: '8px 18px', cursor: 'pointer', border: 'none', letterSpacing: '1px', fontWeight: '800', fontFamily: 'monospace',
                        background: ingestionSubTab === sub.id ? 'var(--color-savings)' : 'transparent',
                        color: ingestionSubTab === sub.id ? '#000' : '#555',
                        transition: 'all 0.15s ease'
                      }}
                    >{sub.label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── PIPELINE SUB-TAB ── */}
            {ingestionSubTab === 'pipeline' && (
              <div style={{ padding: '30px' }}>

                {/* Pipeline Controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSatellite style={{ color: 'var(--color-savings)' }} /> INGESTION_PIPELINE // CONTROL_CENTRE
                  </h3>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input type="file" id="ingestion-csv-upload" style={{ display: 'none' }} accept=".csv"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('csvFile', file);
                        try {
                          setIsSyncing(true);
                          const res = await api.post('/api/retailer/products/bulk/csv', formData);
                          alert(`IMPORT_COMPLETE: ${res.data.result.nUpserted || 0} DEALS_CAPTURED`);
                        } catch (err) { alert('IMPORT_FAILED_CHECK_FORMAT'); }
                        finally { setIsSyncing(false); }
                      }}
                    />
                    <button className="brutalist-btn-outline" style={{ fontSize: '10px' }} onClick={() => document.getElementById('ingestion-csv-upload').click()} disabled={isSyncing}>
                      <FaDownload style={{ marginRight: '8px' }} /> {isSyncing ? 'INGESTING...' : 'UPLOAD_CSV_FEED'}
                    </button>
                    <button className="brutalist-btn-dark" style={{ background: 'var(--color-savings)', color: '#000', border: 'none', fontSize: '10px' }} onClick={handleMarketDiscovery} disabled={isDiscovering}>
                      <FaSatellite style={{ marginRight: '8px' }} className={isDiscovering ? 'fa-spin' : ''} /> {isDiscovering ? 'DISCOVERING...' : 'DISCOVER_MERCHANTS'}
                    </button>
                  </div>
                </div>

                {/* System Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '40px' }}>
                  {[
                    { label: 'PENDING_AUTHORIZATION', value: discoveredMerchants.length, color: '#ff9800' },
                    { label: 'LIVE_PRODUCTS', value: reports?.totalProducts || 0, color: 'var(--color-savings)' },
                    { label: 'GUARD_REJECTIONS', value: 0, color: '#ff5252' },
                    { label: 'INGEST_LATENCY', value: '--s', color: '#fff' }
                  ].map(stat => (
                    <div key={stat.label} className="retailer-card" style={{ margin: '0', padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a' }}>
                      <div style={{ fontSize: '8px', opacity: 0.5, marginBottom: '5px', letterSpacing: '1px' }}>{stat.label}</div>
                      <div style={{ fontSize: '22px', fontWeight: '900', color: stat.color, fontFamily: 'monospace' }}>{stat.value}</div>
                    </div>
                  ))}
                </div>

                {/* Discovered Merchant Nodes */}
                <div style={{ marginBottom: '40px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaNetworkWired /> DISCOVERED_MERCHANT_NODES
                    </h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button className="brutalist-btn-outline" style={{ fontSize: '9px', padding: '5px 15px' }} onClick={() => discoveredMerchants.forEach(m => handleAuthorizeBrand(m.name, m.provider))}>
                        AUTHORIZE_ALL_DETECTED
                      </button>
                      <button className="brutalist-btn-outline" style={{ fontSize: '9px', padding: '5px 15px' }} onClick={() => { fetchBrands(); fetchDiscovery(); }}>
                        <FaSyncAlt /> REFRESH
                      </button>
                    </div>
                  </div>
                  <div style={{ background: '#050505', border: '1px solid #111' }}>
                    <table className="brand-table" style={{ border: 'none' }}>
                      <thead style={{ background: '#0a0a0a' }}>
                        <tr>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>MERCHANT_NAME</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>SOURCE_PROTOCOL</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>DETECTED_ITEMS</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>PIPELINE_STATUS</th>
                          <th style={{ fontSize: '9px', color: '#444', letterSpacing: '1px' }}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {discoveredMerchants?.length === 0 ? (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '60px', opacity: 0.2, fontSize: '11px', fontFamily: 'monospace' }}>
                              NO_MERCHANTS_DISCOVERED... RUN_DISCOVERY_PROTOCOL_TO_BEGIN
                            </td>
                          </tr>
                        ) : (
                          discoveredMerchants?.map((m, idx) => {
                            const isLive = activeBrands?.some(b => b.name === m.name && b.status === 'active');
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #0f0f0f' }}>
                                <td style={{ fontWeight: '900', color: '#fff' }}>{m.name}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#555' }}>{m.provider}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '10px', color: 'var(--color-savings)' }}>{m.items?.toLocaleString()}</td>
                                <td>
                                  <span style={{
                                    fontSize: '9px', padding: '3px 8px',
                                    background: isLive ? 'rgba(0,230,118,0.1)' : 'rgba(255,255,255,0.04)',
                                    color: isLive ? 'var(--color-savings)' : '#666',
                                    border: isLive ? '1px solid var(--color-savings)' : '1px solid #222'
                                  }}>
                                    {isLive ? 'LIVE' : 'PENDING'}
                                  </span>
                                </td>
                                <td>
                                  {isLive ? (
                                    <button className="reject-button" style={{ padding: '4px 10px', fontSize: '9px' }} onClick={() => handleOffboardBrand(activeBrands?.find(b => b.name === m.name)?._id)}>OFFBOARD</button>
                                  ) : (
                                    <button className="refresh-button" style={{ padding: '4px 10px', fontSize: '9px' }} onClick={() => handleAuthorizeBrand(m.name, m.provider)}>AUTHORIZE</button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Universal Schema Preview */}
                <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaCode style={{ color: 'var(--color-savings)' }} /> UNIVERSAL_PRODUCT_SCHEMA // TARGET_STRUCTURE
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      { field: 'title', type: 'String', status: 'MAPPED' },
                      { field: 'brand', type: 'String', status: 'MAPPED' },
                      { field: 'gender', type: 'Enum', status: 'MAPPED' },
                      { field: 'category', type: 'String', status: 'MAPPED' },
                      { field: 'retailer', type: 'String', status: 'MAPPED' },
                      { field: 'price', type: 'Number', status: 'MAPPED' },
                      { field: 'currency', type: 'String', status: 'MAPPED' },
                      { field: 'images', type: 'Array', status: 'MAPPED' },
                      { field: 'affiliateUrl', type: 'String', status: 'MAPPED' },
                      { field: 'sourceType', type: 'Enum', status: 'MAPPED' },
                      { field: 'availability', type: 'Boolean', status: 'MAPPED' },
                      { field: 'variants', type: 'Array', status: 'ROADMAP' },
                      { field: 'scrapedAt', type: 'Date', status: 'MAPPED' },
                      { field: 'syncedAt', type: 'Date', status: 'MAPPED' }
                    ].map(f => (
                      <div key={f.field} style={{ padding: '8px 12px', background: '#000', border: '1px solid #111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontSize: '10px', color: '#fff', fontFamily: 'monospace', fontWeight: '900' }}>{f.field}</span>
                          <span style={{ fontSize: '8px', color: '#444', marginLeft: '8px' }}>{f.type}</span>
                        </div>
                        <span style={{ fontSize: '8px', color: f.status === 'MAPPED' ? 'var(--color-savings)' : '#2196f3', fontFamily: 'monospace' }}>{f.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── NORMALIZER SUB-TAB ── */}
            {ingestionSubTab === 'normalizer' && (
              <div style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaShieldAlt style={{ color: 'var(--color-savings)' }} /> DATA_NORMALIZER // PROCESSING_RULES
                  </h3>
                  <div style={{ display: 'flex', gap: '20px', fontSize: '9px', color: '#444', fontFamily: 'monospace' }}>
                    <span>PROCESSING_MODE: AUTO_OPTIMIZE</span>
                    <span>ACTIVE_FILTERS: DISCOUNT_GUARD_v1.2</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                  {/* Market Guard Rules */}
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaShieldAlt style={{ color: 'var(--color-savings)' }} /> MARKET_GUARD_RULES
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'MINIMUM_DISCOUNT_THRESHOLD', value: '5.0%', border: '#1a1a1a', color: 'var(--color-savings)' },
                        { label: 'AUTO_CURRENCY_CONVERSION (AUD)', value: 'ENABLED', border: '#1a1a1a', color: 'var(--color-savings)' },
                        { label: 'STRIP_EXTERNAL_HTML_TAGS', value: 'ENABLED', border: '#1a1a1a', color: 'var(--color-savings)' },
                        { label: 'MARGIN_PROTECTION_PROTOCOL', value: 'MIN 5.0%', border: '#ff5252', color: '#ff5252' },
                        { label: 'LOGISTICS_INTEGRITY_GUARD', value: 'DOMESTIC_PRIORITY', border: '#2196f3', color: '#2196f3' },
                        { label: 'DUPLICATE_SKU_DETECTION', value: 'ENABLED', border: '#1a1a1a', color: 'var(--color-savings)' }
                      ].map(rule => (
                        <div key={rule.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#000', border: `1px solid ${rule.border}22` }}>
                          <span style={{ fontSize: '10px', color: rule.border !== '#1a1a1a' ? rule.color : '#aaa' }}>{rule.label}</span>
                          <span style={{ color: rule.color, fontWeight: '900', fontSize: '10px', fontFamily: 'monospace' }}>{rule.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Taxonomy Mapping */}
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaMicrochip /> TAXONOMY_MAPPING // CATEGORY_NORMALISATION
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { source: "Men's Coats", target: 'JACKETS', confidence: '98%' },
                        { source: 'Summer Footwear', target: 'SHOES', confidence: '95%' },
                        { source: 'Knitwear', target: 'SWEATERS', confidence: '92%' },
                        { source: 'Swim Shorts', target: 'SWIMWEAR', confidence: '97%' },
                        { source: 'Activewear', target: 'ACTIVEWEAR', confidence: '99%' },
                        { source: 'Casual Tops', target: 'T-SHIRTS', confidence: '88%' }
                      ].map(mapping => (
                        <div key={mapping.source} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#000', border: '1px solid #111' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '10px', color: '#666', fontFamily: 'monospace' }}>{mapping.source}</span>
                            <span style={{ color: '#333', fontSize: '10px' }}>→</span>
                            <span style={{ fontSize: '10px', color: '#fff', fontWeight: '900', fontFamily: 'monospace' }}>{mapping.target}</span>
                          </div>
                          <span style={{ fontSize: '9px', color: 'var(--color-savings)', fontFamily: 'monospace' }}>{mapping.confidence}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Data Transformation Pipeline */}
                <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaExchangeAlt style={{ color: 'var(--color-savings)' }} /> DATA_TRANSFORMATION_PIPELINE
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0', overflowX: 'auto', paddingBottom: '10px' }}>
                    {[
                      { step: '01', label: 'RAW_INGEST', desc: 'CSV/API/Scraper', color: '#555' },
                      { step: '02', label: 'SCHEMA_VALIDATE', desc: 'Field mapping', color: '#2196f3' },
                      { step: '03', label: 'PRICE_GUARD', desc: 'Min discount check', color: '#ff9800' },
                      { step: '04', label: 'NORMALISE', desc: 'Category + currency', color: 'var(--color-savings)' },
                      { step: '05', label: 'DEDUPLICATE', desc: 'SKU collision check', color: '#9c27b0' },
                      { step: '06', label: 'PUBLISH', desc: 'Live on platform', color: 'var(--color-savings)' }
                    ].map((step, i, arr) => (
                      <React.Fragment key={step.step}>
                        <div style={{ textAlign: 'center', minWidth: '120px', padding: '15px 10px', background: '#000', border: `1px solid ${step.color}33` }}>
                          <div style={{ fontSize: '18px', fontWeight: '900', color: step.color, fontFamily: 'monospace', marginBottom: '6px' }}>{step.step}</div>
                          <div style={{ fontSize: '10px', fontWeight: '900', color: '#fff', marginBottom: '4px' }}>{step.label}</div>
                          <div style={{ fontSize: '8px', color: '#555' }}>{step.desc}</div>
                        </div>
                        {i < arr.length - 1 && (
                          <div style={{ fontSize: '16px', color: '#333', padding: '0 8px', flexShrink: 0 }}>→</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── ORCHESTRATOR SUB-TAB ── */}
            {ingestionSubTab === 'orchestrator' && (
              <div style={{ padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaBullseye style={{ color: 'var(--color-savings)' }} /> MERCHANDISING_ORCHESTRATOR // PLACEMENT_CONTROL
                  </h3>
                  <button className="brutalist-btn-dark" style={{ background: 'var(--color-savings)', color: '#000', border: 'none', fontSize: '10px' }} onClick={() => fetchData()} disabled={isSyncing}>
                    <FaMagic style={{ marginRight: '8px' }} className={isSyncing ? 'fa-spin' : ''} /> {isSyncing ? 'RECALIBRATING...' : 'RE-SYNC_PLACEMENT_LOGIC'}
                  </button>
                </div>

                {/* Placement Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '40px' }}>
                  {[
                    { id: 'featured', label: 'FEATURED_PAGE' },
                    { id: 'trending', label: 'TRENDING_FEED' },
                    { id: 'new_in', label: 'NEW_IN_ARRIVALS' }
                  ].map(page => (
                    <div key={page.id} style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '12px', fontWeight: '900', margin: 0 }}>{page.label}</h3>
                        <FaBullseye style={{ color: 'var(--color-savings)', fontSize: '14px' }} />
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '8px', color: '#444', marginBottom: '6px' }}>CURRENTLY_PLACED</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', minHeight: '24px' }}>
                          {activeBrands.filter(b => b.status === 'active' && b.featuredIn?.includes(page.id)).slice(0, 3).map(b => (
                            <span key={b._id} style={{ fontSize: '9px', background: '#111', padding: '3px 8px', border: '1px solid #222' }}>{b.name?.toUpperCase()}</span>
                          ))}
                          {activeBrands.filter(b => b.status === 'active' && b.featuredIn?.includes(page.id)).length > 3 && (
                            <span style={{ fontSize: '9px', opacity: 0.3 }}>+{activeBrands.filter(b => b.status === 'active' && b.featuredIn?.includes(page.id)).length - 3} MORE</span>
                          )}
                          {activeBrands.filter(b => b.status === 'active' && b.featuredIn?.includes(page.id)).length === 0 && (
                            <span style={{ fontSize: '9px', opacity: 0.3 }}>VACANT</span>
                          )}
                        </div>
                      </div>
                      <div style={{ marginBottom: '15px', maxHeight: '160px', overflowY: 'auto' }}>
                        <div style={{ fontSize: '8px', color: '#444', marginBottom: '8px' }}>ASSIGN_BRANDS</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          {activeBrands?.filter(b => b.status === 'active')?.map(brand => (
                            <label key={brand._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '10px', background: '#000', padding: '5px 10px', border: '1px solid #111', cursor: 'pointer' }}>
                              <input type="checkbox" checked={brand.featuredIn?.includes(page.id)} onChange={(e) => handleUpdateOrchestration(brand._id, page.id, e.target.checked)} style={{ accentColor: 'var(--color-savings)' }} />
                              {brand.name?.toUpperCase()}
                            </label>
                          ))}
                          {activeBrands?.filter(b => b.status === 'active')?.length === 0 && (
                            <div style={{ fontSize: '9px', opacity: 0.3 }}>NO_ACTIVE_BRANDS</div>
                          )}
                        </div>
                      </div>
                      <div style={{ background: '#000', border: '1px solid #111', padding: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', marginBottom: '5px' }}>
                          <span style={{ color: '#444' }}>SMART_WEIGHTING</span>
                          <span style={{ color: 'var(--color-savings)' }}>HIGH (85%)</span>
                        </div>
                        <div style={{ width: '100%', height: '2px', background: '#111' }}>
                          <div style={{ width: '85%', height: '100%', background: 'var(--color-savings)' }}></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Automation Engine + Diversity Guard */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaMagic style={{ color: 'var(--color-savings)' }} /> AUTOMATED_PROMOTION_ENGINE
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {[
                        { label: 'SYNC_WITH_TOP_PERFORMERS', desc: 'Auto-feature brands with > 3% conversion rate.', status: 'ACTIVE', color: 'var(--color-savings)' },
                        { label: 'NEW_NODE_AUTO_DISCOVERY', desc: 'Promote newly onboarded brands to "New In" for 7 days.', status: 'ACTIVE', color: 'var(--color-savings)' },
                        { label: 'AFFILIATE_PRIORITY_BOOST', desc: 'Boost Rakuten affiliate products in trending feed.', status: 'PENDING', color: '#ff9800' }
                      ].map(rule => (
                        <div key={rule.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#000', border: '1px solid #111' }}>
                          <div>
                            <div style={{ fontSize: '10px', fontWeight: '900' }}>{rule.label}</div>
                            <div style={{ fontSize: '8px', color: '#444', marginTop: '3px' }}>{rule.desc}</div>
                          </div>
                          <div style={{ color: rule.color, fontSize: '10px', fontWeight: '900', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>[{rule.status}]</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: '#050505', border: '1px solid #111', padding: '25px' }}>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaShieldAlt style={{ color: 'var(--color-savings)' }} /> DIVERSITY_GUARD_v2
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <p style={{ fontSize: '10px', color: '#666', lineHeight: '1.6' }}>
                        Prevents single-brand dominance. Ensures feed variety by limiting consecutive items from the same retailer node.
                      </p>
                      <div style={{ background: '#000', border: '1px solid #111', padding: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '10px' }}>
                          <span>MAX_CONSECUTIVE_ITEMS</span>
                          <span style={{ fontWeight: '900' }}>3_LIMIT</span>
                        </div>
                        <input type="range" style={{ width: '100%', accentColor: 'var(--color-savings)' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '0 5px' }}>
                        <span style={{ opacity: 0.5 }}>VARIETY_LEVEL:</span>
                        <span style={{ color: 'var(--color-savings)', fontWeight: '900' }}>OPTIMIZED_SURGICAL</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        );

      case 'conflict':
        return (
          <div className="profile-section animate-slide-up" style={{ background: '#000', padding: '0', border: '1px solid #1a1a1a' }}>
            {/* --- CONFLICT ENGINE HEADER --- */}
            <div style={{ padding: '30px', borderBottom: '1px solid #1a1a1a', background: 'linear-gradient(to bottom, #050505, #000)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '24px', letterSpacing: '2px', fontWeight: '900', color: '#fff', marginBottom: '5px' }}>COLLISION_MATRIX // SKU_DE-DUP</h2>
                  <div style={{ display: 'flex', gap: '20px', fontSize: '10px', color: '#666', fontFamily: 'monospace' }}>
                    <span>ENGINE_STATUS: {isSyncing ? 'SCANNING...' : 'IDLE'}</span>
                    <span>INTEGRITY_SHIELD: ACTIVE</span>
                  </div>
                </div>
                <button className="brutalist-btn-dark" style={{ background: '#ff5252', color: '#000', border: 'none' }} onClick={handleExecuteCollisionScan} disabled={isSyncing}>
                  <FaShieldAlt style={{ marginRight: '8px' }} className={isSyncing ? 'fa-spin' : ''} /> {isSyncing ? 'SCANNING...' : 'EXECUTE_FULL_SCAN'}
                </button>
              </div>
            </div>

            <div style={{ padding: '30px' }}>
              {/* --- COLLISION STATS --- */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '40px' }}>
                <div className="retailer-card" style={{ margin: '0', padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a' }}>
                  <div style={{ fontSize: '8px', opacity: 0.5, marginBottom: '5px' }}>TOTAL_SKU_COLLISIONS</div>
                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#ff5252' }}>{collisions.length}</div>
                </div>
                <div className="retailer-card" style={{ margin: '0', padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a' }}>
                  <div style={{ fontSize: '8px', opacity: 0.5, marginBottom: '5px' }}>CONSUMER_SAVINGS_PROTECTED</div>
                  <div style={{ fontSize: '20px', fontWeight: '900', color: 'var(--color-savings)' }}>$0.00</div>
                </div>
                <div className="retailer-card" style={{ margin: '0', padding: '20px', background: '#0a0a0a', border: '1px solid #1a1a1a' }}>
                  <div style={{ fontSize: '8px', opacity: 0.5, marginBottom: '5px' }}>AUTO_RESOLVED_ISSUES</div>
                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#fff' }}>0</div>
                </div>
              </div>

              {/* --- ACTIVE COLLISIONS MANIFEST --- */}
              <div style={{ marginBottom: '40px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaExchangeAlt /> DATA_CONFLICT_MANIFEST
                  </h3>
                </div>
                <div style={{ background: '#050505', border: '1px solid #111' }}>
                  <table className="brand-table" style={{ border: 'none' }}>
                    <thead style={{ background: '#0a0a0a' }}>
                      <tr>
                        <th style={{ fontSize: '10px', color: '#444' }}>SKU_REF</th>
                        <th style={{ fontSize: '10px', color: '#444' }}>PRODUCT_IDENTIFIER</th>
                        <th style={{ fontSize: '10px', color: '#444' }}>NODE_SOURCES</th>
                        <th style={{ fontSize: '10px', color: '#444' }}>PRICE_VARIANCE</th>
                        <th style={{ fontSize: '10px', color: '#444' }}>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collisions?.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '60px', opacity: 0.2, fontSize: '11px', fontFamily: 'monospace' }}>
                            _ZERO_COLLISIONS_DETECTED... DATA_INTEGRITY_OPTIMAL
                          </td>
                        </tr>
                      ) : (
                        collisions?.map((c, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #111' }}>
                            <td style={{ fontFamily: 'monospace', fontSize: '10px' }}>{c.sku}</td>
                            <td style={{ fontWeight: '900', color: '#fff' }}>{c.name}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '10px' }}>{c.sources.join(', ')}</td>
                            <td style={{ color: 'var(--color-savings)', fontWeight: 'bold' }}>{c.variance}</td>
                            <td><button className="brutalist-btn-outline" style={{ fontSize: '9px', padding: '5px 10px' }}>RESOLVE</button></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* --- RESOLUTION LOGIC --- */}
              <div style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ background: '#050505', border: '1px solid #111', padding: '20px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaTerminal style={{ color: 'var(--color-savings)' }} /> AUTOMATED_RESOLUTION_STRATEGY
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#000', border: '1px solid #111' }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold' }}>PRIORITIZE_LOWEST_PRICE</div>
                        <div style={{ fontSize: '8px', color: '#444' }}>Always show the node with the highest discount.</div>
                      </div>
                      <div style={{ color: 'var(--color-savings)', fontSize: '10px', fontWeight: '900' }}>[ACTIVE]</div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#000', border: '1px solid #111' }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold' }}>PRIORITIZE_TRUSTED_NODE</div>
                        <div style={{ fontSize: '8px', color: '#444' }}>Override price if source reliability is higher.</div>
                      </div>
                      <div style={{ color: '#444', fontSize: '10px', fontWeight: '900' }}>[INACTIVE]</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'advertising':
        return (
          <div className="profile-section animate-fade-in" style={{ background: '#000', padding: '0', border: 'none' }}>
            {/* --- ADVERTISING COMMAND CENTER --- */}
            <div style={{ padding: '30px', borderBottom: '1px solid #1a1a1a', background: 'linear-gradient(to bottom, #050505, #000)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '24px', letterSpacing: '2px', fontWeight: '900', color: '#fff', marginBottom: '5px', border: 'none', padding: '0' }}>ADVERTISING_LAB // CREATIVE_GENERATOR</h2>
                  <div style={{ display: 'flex', gap: '20px', fontSize: '10px', color: '#666', fontFamily: 'monospace' }}>
                    <span>RENDERING_ENGINE: HTML_CSS_v4</span>
                    <span>OPTIMIZED_ASPECT_RATIOS: ACTIVE</span>
                  </div>
                </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      className="brutalist-btn brutalist-btn-dark"
                      onClick={selectedTemplate === 'official_partner' ? handleDownloadVideo : handleDownloadImage}
                      disabled={isDownloading}
                      style={{ fontSize: '10px', padding: '10px 20px', background: '#fff', color: '#000', border: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <FaDownload /> {isDownloading ? 'PROCESSING...' : (selectedTemplate === 'brand_story' ? 'DOWNLOAD_CAROUSEL_ZIP' : 'GENERATE_&_DOWNLOAD')}
                    </button>
                    <button 
                      onClick={() => setShowAdOverlay(true)}
                      style={{ fontSize: '10px', padding: '10px 20px', background: 'var(--color-savings)', color: '#000', border: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <FaMagic /> POP_OUT_FOR_SNIPPET
                    </button>
                  </div>
              </div>

              {/* --- WORKFLOW PROTOCOL NAVIGATION --- */}
              <div style={{ display: 'flex', gap: '5px', marginTop: '10px', background: '#111', padding: '2px', border: '1px solid #222' }}>
                {[
                  { id: 'blueprint', label: '01 // CHOOSE_BLUEPRINT', icon: <FaLayerGroup /> },
                  { id: 'config', label: '02 // SYNC_ASSETS', icon: <FaSlidersH /> },
                  { id: 'manifest', label: '03 // FINAL_MANIFEST', icon: <FaBullseye /> }
                ].map((tab, idx) => (
                  <button 
                    key={tab.id}
                    onClick={() => setAdSubTab(tab.id)}
                    style={{
                      flex: 1,
                      padding: '15px',
                      background: adSubTab === tab.id ? 'var(--color-savings)' : '#000',
                      border: 'none',
                      color: adSubTab === tab.id ? '#000' : '#444',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10px',
                      fontWeight: '900',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative'
                    }}
                  >
                    {tab.icon} {tab.label}
                    {idx < 2 && (
                      <div style={{ position: 'absolute', right: '-10px', zIndex: 10, color: '#222', fontSize: '20px' }}>
                        <FaMagic style={{ opacity: 0.1 }} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* --- WORKFLOW: STEP 01 (BLUEPRINT) --- */}
            {adSubTab === 'blueprint' && (
              <div className="animate-slide-up" style={{ padding: '40px', background: '#0a0a0a', minHeight: '800px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', margin: 0 }}>SELECT_VISUAL_DNA_FOR_DEPLOYMENT</h3>
                  <div style={{ fontSize: '9px', color: 'var(--color-savings)', fontFamily: 'monospace' }}>ACTIVE_TEMPLATES: {templates.length}</div>
                </div>

                <div style={{ background: '#050505', border: '1px solid #1a1a1a' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#000', borderBottom: '1px solid #222' }}>
                      <tr>
                        <th style={{ padding: '20px', textAlign: 'left', fontSize: '10px', color: '#444', fontFamily: 'monospace', width: '50px' }}>ORDER</th>
                        <th style={{ padding: '20px', textAlign: 'left', fontSize: '10px', color: '#444', fontFamily: 'monospace', width: '150px' }}>PREVIEW</th>
                        <th style={{ padding: '20px', textAlign: 'left', fontSize: '10px', color: '#444', fontFamily: 'monospace' }}>TEMPLATE_SPEC</th>
                        <th style={{ padding: '20px', textAlign: 'left', fontSize: '10px', color: '#444', fontFamily: 'monospace' }}>DESCRIPTION</th>
                        <th style={{ padding: '20px', textAlign: 'right', fontSize: '10px', color: '#444', fontFamily: 'monospace', width: '150px' }}>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((t, index) => (
                        <tr 
                          key={t.id}
                          onClick={() => {
                            setSelectedTemplate(t.id);
                            if (t.id === 'brand_story') {
                              setAdViewMode('square');
                              setBrandStorySlideIndex(0);
                            }
                            setAdSubTab('config');
                          }}
                          style={{ 
                            borderBottom: '1px solid #111', 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            background: selectedTemplate === t.id ? 'rgba(0, 230, 118, 0.02)' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#0d0d0d'}
                          onMouseLeave={(e) => e.currentTarget.style.background = selectedTemplate === t.id ? 'rgba(0, 230, 118, 0.02)' : 'transparent'}
                        >
                          <td style={{ padding: '15px 20px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                              {index > 0 && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); moveTemplate(index, 'up'); }}
                                  style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', padding: '5px' }}
                                  onMouseEnter={(e) => e.target.style.color = 'var(--color-savings)'}
                                  onMouseLeave={(e) => e.target.style.color = '#444'}
                                >
                                  <FaArrowUp />
                                </button>
                              )}
                              {index < templates.length - 1 && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); moveTemplate(index, 'down'); }}
                                  style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', padding: '5px' }}
                                  onMouseEnter={(e) => e.target.style.color = 'var(--color-savings)'}
                                  onMouseLeave={(e) => e.target.style.color = '#444'}
                                >
                                  <FaArrowDown />
                                </button>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '15px 20px' }}>
                            <div style={{ 
                              width: '120px', 
                              height: '80px', 
                              background: '#000', 
                              border: `1px solid ${selectedTemplate === t.id ? 'var(--color-savings)' : '#222'}`,
                              display: 'flex',
                              flexDirection: 'column',
                              padding: '6px',
                              gap: '4px',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              {/* --- HIGH-FIDELITY TEMPLATE MINIATURES --- */}
                              
                              {t.id === 'official_partner' && (
                                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#000', position: 'relative' }}>
                                  <div style={{ width: '100%', height: '100%', opacity: 0.15, background: 'linear-gradient(45deg, #222 25%, transparent 25%, transparent 50%, #222 50%, #222 75%, transparent 75%, transparent)' , backgroundSize: '10px 10px' }}></div>
                                  <div style={{ position: 'absolute', width: '70%', height: '2px', background: 'var(--color-savings)' }}></div>
                                  <div style={{ position: 'absolute', top: '15px', left: '15px', fontSize: '6px', color: 'var(--color-savings)', fontWeight: '900' }}>OFFICIAL</div>
                                </div>
                              )}

                              {t.id === 'blank_canvas' && (
                                <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#070707' }}>
                                  <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 70% 20%, rgba(0,230,118,0.25) 0%, transparent 50%)' }} />
                                  <div style={{ position: 'absolute', top: '6px', left: '6px', fontSize: '5px', color: '#00E676', fontFamily: 'monospace', letterSpacing: '0.5px' }}>● LIVE</div>
                                  <div style={{ position: 'absolute', top: '6px', right: '6px', background: '#00E676', color: '#04140a', padding: '3px 4px', fontSize: '8px', fontWeight: '900', lineHeight: 1, fontFamily: 'Impact, Bebas Neue, sans-serif' }}>50%</div>
                                  <div style={{ position: 'absolute', left: '6px', right: '6px', bottom: '8px' }}>
                                    <div style={{ fontSize: '9px', fontWeight: '900', letterSpacing: '-0.03em', lineHeight: 0.9, color: '#fff', fontFamily: 'Impact, Bebas Neue, sans-serif' }}>THIS DEAL<br/>IS LIVE</div>
                                    <div style={{ marginTop: '4px', height: '8px', background: '#fff', display: 'flex', alignItems: 'center', padding: '0 3px', fontSize: '4px', fontWeight: '900', color: '#000', letterSpacing: '0.5px' }}>GET THE DEAL</div>
                                  </div>
                                </div>
                              )}

                              {t.id === 'brand_story' && (
                                <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#000' }}>
                                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,230,118,0.25) 0%, transparent 45%)' }} />
                                  <div style={{ position: 'absolute', inset: '6px 14px 10px 6px', border: '1px solid #1a1a1a', background: '#050505', transform: 'rotate(-4deg)' }} />
                                  <div style={{ position: 'absolute', inset: '10px 10px 10px 10px', border: '1px solid #222', background: '#0a0a0a', transform: 'rotate(2deg)' }} />
                                  <div style={{ position: 'absolute', inset: '14px 6px 6px 14px', border: '1px solid #00E676', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '8px' }}>
                                    <div style={{ position: 'absolute', top: '6px', right: '6px', fontSize: '5px', color: '#00E676', fontFamily: 'monospace' }}>01/05</div>
                                    <div style={{ fontSize: '10px', fontWeight: '900', letterSpacing: '-0.04em', lineHeight: 0.85, fontFamily: 'Impact, Bebas Neue, sans-serif' }}>
                                      <span style={{ color: '#00E676' }}>Stock</span><br /><span style={{ color: '#fff' }}>room</span>
                                    </div>
                                    <div style={{ marginTop: '4px', height: '2px', width: '100%', background: '#00E676' }} />
                                  </div>
                                </div>
                              )}

                              {t.id === 'ad_creative' && (
                                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'radial-gradient(circle, #111 0%, #000 100%)', gap: '3px' }}>
                                  <div style={{ width: '30px', height: '1px', background: '#333' }}></div>
                                  <div style={{ fontSize: '10px', color: '#fff', fontWeight: '900', letterSpacing: '-0.5px' }}>ACCESS</div>
                                  <div style={{ width: '30px', height: '1px', background: 'var(--color-savings)' }}></div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '20px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '900', color: selectedTemplate === t.id ? 'var(--color-savings)' : '#fff', letterSpacing: '1px' }}>{t.name}</div>
                            <div style={{ fontSize: '8px', color: '#444', fontFamily: 'monospace', marginTop: '4px' }}>UID: {t.id.toUpperCase()}</div>
                          </td>
                          <td style={{ padding: '20px' }}>
                            <div style={{ fontSize: '11px', color: '#888', maxWidth: '400px', lineHeight: '1.4' }}>{t.description}</div>
                          </td>
                          <td style={{ padding: '20px', textAlign: 'right' }}>
                            <span style={{ 
                              fontSize: '9px', 
                              padding: '4px 10px', 
                              background: selectedTemplate === t.id ? 'var(--color-savings)' : '#111',
                              color: selectedTemplate === t.id ? '#000' : '#444',
                              fontWeight: '900',
                              fontFamily: 'var(--font-mono)'
                            }}>
                              {selectedTemplate === t.id ? '>>_SELECTED' : '>>_READY'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button 
                    className="brutalist-btn-dark" 
                    onClick={() => setAdSubTab('config')}
                    style={{ background: '#fff', color: '#000', padding: '15px 40px', fontWeight: '900' }}
                  >
                    INITIALIZE_PROTOCOL >>
                  </button>
                </div>
              </div>
            )}

            {/* --- WORKFLOW: STEP 02 (ASSET_SYNC) --- */}
            {adSubTab === 'config' && (
              <div className="animate-slide-up" style={{ background: '#0a0a0a', minHeight: '800px', padding: '40px' }}>
                {selectedTemplate === 'brand_story' ? (
                  <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '28px' }}>
                    <div>
                      <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '12px' }}>01 // STORY_LOCKED</h3>
                      <p style={{ fontSize: '13px', color: '#aaa', lineHeight: 1.5, marginBottom: '8px' }}>
                        Brand Story is a fixed 5-slide Instagram carousel. No product sync required — export is ready.
                      </p>
                      <div style={{ fontSize: '10px', color: '#00E676', fontFamily: 'monospace', letterSpacing: '1px' }}>
                        FORMAT: FEED 1:1 · 1080×1080 · 5 SLIDES
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                      {BRAND_STORY_SLIDES.map((slide, idx) => (
                        <button
                          key={slide.id}
                          type="button"
                          onClick={() => {
                            setBrandStorySlideIndex(idx);
                            setAdViewMode('square');
                            setAdSubTab('manifest');
                          }}
                          style={{
                            padding: '16px 10px',
                            background: '#000',
                            border: '1px solid #222',
                            color: '#fff',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <div style={{ fontSize: '8px', color: '#00E676', fontFamily: 'monospace', marginBottom: '8px' }}>{slide.label}</div>
                          <div style={{ fontSize: '11px', fontWeight: '800' }}>{slide.title}</div>
                        </button>
                      ))}
                    </div>

                    <div style={{ background: '#050505', border: '1px solid #1a1a1a', padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ fontSize: '9px', color: '#666', fontFamily: 'monospace' }}>INSTAGRAM_CAPTION</div>
                        <button
                          type="button"
                          onClick={copyBrandStoryCaption}
                          style={{
                            background: 'none',
                            border: '1px solid #333',
                            color: captionCopied ? '#00E676' : '#fff',
                            fontSize: '9px',
                            fontWeight: '800',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <FaCopy /> {captionCopied ? 'COPIED' : 'COPY_CAPTION'}
                        </button>
                      </div>
                      <pre style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'var(--font-mono, monospace)',
                        fontSize: '11px',
                        color: '#ccc',
                        lineHeight: 1.55,
                        margin: 0,
                      }}>{BRAND_STORY_CAPTION}</pre>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setAdViewMode('square');
                        setAdSubTab('manifest');
                      }}
                      style={{
                        width: '100%',
                        padding: '18px',
                        background: 'var(--color-savings)',
                        color: '#000',
                        border: 'none',
                        fontWeight: '900',
                        fontSize: '12px',
                        letterSpacing: '1px',
                        cursor: 'pointer',
                      }}
                    >
                      CONTINUE_TO_FINAL_MANIFEST →
                    </button>
                  </div>
                ) : selectedTemplate === 'official_partner' ? (
                  <div style={{ display: 'flex', gap: '40px' }}>
                    <div style={{ width: '400px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
                      <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px' }}>01 // PARTNER_ASSET_INGEST</h3>
                      <p style={{ fontSize: '10px', color: '#444', marginBottom: '20px' }}>Upload high-fidelity brand banners for the background manifest.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div className="retailer-card" style={{ padding: '15px', background: '#000', margin: 0 }}>
                          <span style={{ fontSize: '9px', fontWeight: '800', opacity: 0.6, display: 'block', marginBottom: '10px' }}>DESKTOP_SOURCE (21:9)</span>
                          <label className="brutalist-btn brutalist-btn-outline" style={{ display: 'block', padding: '15px', textAlign: 'center', fontSize: '9px', cursor: 'pointer', borderStyle: 'dashed' }}>
                            {partnerDesktopBanner ? 'FILE_LOADED' : 'SELECT_FILE (JPG/GIF/MP4)'}
                            <input type="file" hidden accept="image/*,video/mp4" onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.type === 'image/gif') {
                                  const buffer = await file.arrayBuffer();
                                  const gif = parseGIF(buffer);
                                  const frames = decompressFrames(gif, true);
                                  const rendered = frames.map(frame => {
                                    const c = document.createElement('canvas');
                                    c.width = frame.dims.width;
                                    c.height = frame.dims.height;
                                    const ctx = c.getContext('2d');
                                    const imageData = ctx.createImageData(frame.dims.width, frame.dims.height);
                                    imageData.data.set(frame.patch);
                                    ctx.putImageData(imageData, 0, 0);
                                    return { canvas: c, delay: frame.delay };
                                  });
                                  setGifFrames(rendered);
                                } else {
                                  setGifFrames([]);
                                }
                                const reader = new FileReader();
                                reader.onloadend = () => setPartnerDesktopBanner(reader.result);
                                reader.readAsDataURL(file);
                              }
                            }} />
                          </label>
                        </div>
                        <div className="retailer-card" style={{ padding: '15px', background: '#000', margin: 0 }}>
                          <span style={{ fontSize: '9px', fontWeight: '800', opacity: 0.6, display: 'block', marginBottom: '10px' }}>MOBILE_SOURCE (9:16)</span>
                          <label className="brutalist-btn brutalist-btn-outline" style={{ display: 'block', padding: '15px', textAlign: 'center', fontSize: '9px', cursor: 'pointer', borderStyle: 'dashed' }}>
                            {partnerMobileBanner ? 'FILE_LOADED' : 'SELECT_FILE (JPG/GIF/MP4)'}
                            <input type="file" hidden accept="image/*,video/mp4" onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.type === 'image/gif') {
                                  const buffer = await file.arrayBuffer();
                                  const gif = parseGIF(buffer);
                                  const frames = decompressFrames(gif, true);
                                  const rendered = frames.map(frame => {
                                    const c = document.createElement('canvas');
                                    c.width = frame.dims.width;
                                    c.height = frame.dims.height;
                                    const ctx = c.getContext('2d');
                                    const imageData = ctx.createImageData(frame.dims.width, frame.dims.height);
                                    imageData.data.set(frame.patch);
                                    ctx.putImageData(imageData, 0, 0);
                                    return { canvas: c, delay: frame.delay };
                                  });
                                  setGifFrames(rendered);
                                } else {
                                  setGifFrames([]);
                                }
                                const reader = new FileReader();
                                reader.onloadend = () => setPartnerMobileBanner(reader.result);
                                reader.readAsDataURL(file);
                              }
                            }} />
                          </label>
                        </div>
                      </div>
                      <div className="retailer-card" style={{ padding: '15px', background: '#000', margin: 0 }}>
                        <span style={{ fontSize: '9px', fontWeight: '800', opacity: 0.6, display: 'block', marginBottom: '10px' }}>PARTNER_TEXT_OVERRIDE</span>
                        <input 
                          type="text" 
                          placeholder="ENTER PARTNER NAME..." 
                          className="retailers-search" 
                          style={{ width: '100%', padding: '12px', fontSize: '11px', background: '#0a0a0a', border: '1px solid #222', color: '#fff', marginBottom: '15px' }}
                          value={partnerCustomText}
                          onChange={(e) => setPartnerCustomText(e.target.value)}
                        />
                        
                        <span style={{ fontSize: '9px', fontWeight: '800', opacity: 0.6, display: 'block', marginBottom: '10px' }}>FONT_SIZE_ADJUST (VW)</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <input 
                            type="range" 
                            min="2" 
                            max="25" 
                            step="0.5"
                            style={{ flex: 1, accentColor: '#00E676' }}
                            value={partnerFontSize}
                            onChange={(e) => setPartnerFontSize(parseFloat(e.target.value))}
                          />
                          <span style={{ fontSize: '12px', color: '#00E676', fontWeight: '900', fontFamily: 'monospace', minWidth: '40px' }}>{partnerFontSize}vw</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ flex: 1, background: '#050505', border: '1px solid #111', padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                      <div style={{ color: 'var(--color-savings)', fontSize: '40px', marginBottom: '20px' }}><FaHandshake /></div>
                      <h4 style={{ color: '#fff', fontSize: '18px', marginBottom: '10px' }}>PROTOCOL_INITIALIZED</h4>
                      <p style={{ color: '#666', fontSize: '12px', maxWidth: '400px' }}>Official Partner template active. Overlay applied to custom banners.</p>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid #222', paddingBottom: '20px', flexWrap: 'wrap', gap: '20px' }}>
                      <div style={{ flex: '1 1 400px' }}>
                        <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '10px' }}>01 // INVENTORY_SYNC_TABLE</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <input 
                              type="text" 
                              placeholder="SEARCH_MARKET_INVENTORY..." 
                              className="retailers-search" 
                              style={{ width: '100%', padding: '12px 12px 12px 40px', fontSize: '11px' }}
                              value={adSearchTerm}
                              onChange={(e) => setAdSearchTerm(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && fetchAdProducts()}
                            />
                            <FaTerminal style={{ position: 'absolute', left: '12px', top: '15px', color: '#444', fontSize: '10px' }} />
                          </div>
                          <button onClick={fetchAdProducts} className="brutalist-btn brutalist-btn-dark" style={{ padding: '0 20px', fontSize: '9px', height: '42px' }}>SYNC_DATA</button>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ textAlign: 'right', paddingRight: '10px', borderRight: '1px solid #222' }}>
                          <div style={{ fontSize: '8px', color: '#444', marginBottom: '2px' }}>ACTIVE_FILTERS</div>
                          <button 
                            onClick={() => {
                              setAdFilters({ brand: [], retailer: [], category: [], gender: [] });
                              setAdSortConfig(null);
                              setAdPage(1);
                            }}
                            disabled={adFilters.brand.length === 0 && adFilters.retailer.length === 0 && adFilters.category.length === 0 && adFilters.gender.length === 0 && !adSortConfig && adPage === 1}
                            style={{ background: 'none', border: 'none', color: (adFilters.brand.length > 0 || adFilters.retailer.length > 0 || adFilters.category.length > 0 || adFilters.gender.length > 0 || adSortConfig || adPage > 1) ? 'var(--color-savings)' : '#222', fontSize: '10px', fontWeight: '900', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '5px' }}
                          >
                            <FaSyncAlt style={{ fontSize: '8px' }} /> RESET_FILTERS
                          </button>
                        </div>

                        <div style={{ textAlign: 'right', paddingRight: '10px', borderRight: '1px solid #222' }}>
                          <div style={{ fontSize: '8px', color: '#444', marginBottom: '2px' }}>SELECTED_ASSETS</div>
                          <div style={{ fontSize: '12px', fontWeight: '900', color: 'var(--color-savings)' }}>{selectedAdProducts.length} <span style={{ color: '#444', fontSize: '9px' }}>/ 50</span></div>
                        </div>

                        <button 
                          onClick={() => setSelectedAdProducts([])} 
                          className="brutalist-btn-outline"
                          style={{ padding: '8px 12px', color: '#ff5252', borderColor: '#ff5252', fontSize: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '80px', height: '42px', justifyContent: 'center' }}
                        >
                          <FaTrash style={{ fontSize: '10px' }} />
                          <span>CLEAR_ALL</span>
                        </button>

                        <button 
                          className="brutalist-btn brutalist-btn-dark" 
                          style={{ background: '#fff', color: '#000', padding: '10px 25px', fontWeight: '900', fontSize: '10px', height: '42px' }} 
                          onClick={() => setAdSubTab('manifest')}
                          disabled={selectedAdProducts.length === 0}
                        >
                          PROCEED_TO_MANIFEST >>
                        </button>
                      </div>
                    </div>

                    <div style={{ background: '#050505', border: '1px solid #1a1a1a', position: 'relative' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ background: '#000', borderBottom: '1px solid #222' }}>
                            <th style={{ padding: '15px 20px', width: '40px' }}>
                              <FaCheck style={{ opacity: 0.2 }} />
                            </th>
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', width: '60px' }}>ASSET</th>
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                              PRODUCT_IDENTIFIER <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'name' ? 1 : 0.3 }} />
                            </th>
                            
                            {/* Category Filter */}
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', position: 'relative' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowFilterDropdown(showFilterDropdown === 'category' ? null : 'category')}>
                                CATEGORY <FaFilter style={{ fontSize: '8px', color: adFilters.category.length > 0 ? 'var(--color-savings)' : '#444' }} />
                                <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'category' ? 1 : 0.3 }} onClick={(e) => { e.stopPropagation(); toggleSort('category'); }} />
                              </div>
                              {showFilterDropdown === 'category' && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, background: '#000', border: '1px solid #333', zIndex: 100, minWidth: '200px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', padding: '10px' }}>
                                  <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="custom-scrollbar">
                                    {uniqueFilterOptions.categories.map(c => (
                                      <div key={c} onClick={() => toggleFilter('category', c)} style={{ padding: '8px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: adFilters.category.includes(c) ? '#111' : 'transparent' }}>
                                        {adFilters.category.includes(c) ? <FaCheck style={{ color: 'var(--color-savings)' }} /> : <div style={{ width: '10px' }} />}
                                        {c.toUpperCase()}
                                      </div>
                                    ))}
                                  </div>
                                  <button onClick={() => setAdFilters(prev => ({ ...prev, category: [] }))} style={{ width: '100%', padding: '8px', background: 'transparent', border: 'none', borderTop: '1px solid #222', color: '#ff5252', fontSize: '9px', cursor: 'pointer', marginTop: '5px' }}>RESET_FILTER</button>
                                </div>
                              )}
                            </th>

                            {/* Brand Filter */}
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', position: 'relative' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowFilterDropdown(showFilterDropdown === 'brand' ? null : 'brand')}>
                                BRAND <FaFilter style={{ fontSize: '8px', color: adFilters.brand.length > 0 ? 'var(--color-savings)' : '#444' }} />
                                <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'brand' ? 1 : 0.3 }} onClick={(e) => { e.stopPropagation(); toggleSort('brand'); }} />
                              </div>
                              {showFilterDropdown === 'brand' && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, background: '#000', border: '1px solid #333', zIndex: 100, minWidth: '200px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', padding: '10px' }}>
                                  <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="custom-scrollbar">
                                    {uniqueFilterOptions.brands.map(b => (
                                      <div key={b} onClick={() => toggleFilter('brand', b)} style={{ padding: '8px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: adFilters.brand.includes(b) ? '#111' : 'transparent' }}>
                                        {adFilters.brand.includes(b) ? <FaCheck style={{ color: 'var(--color-savings)' }} /> : <div style={{ width: '10px' }} />}
                                        {b.toUpperCase()}
                                      </div>
                                    ))}
                                  </div>
                                  <button onClick={() => setAdFilters(prev => ({ ...prev, brand: [] }))} style={{ width: '100%', padding: '8px', background: 'transparent', border: 'none', borderTop: '1px solid #222', color: '#ff5252', fontSize: '9px', cursor: 'pointer', marginTop: '5px' }}>RESET_FILTER</button>
                                </div>
                              )}
                            </th>

                            {/* Retailer Filter */}
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', position: 'relative' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowFilterDropdown(showFilterDropdown === 'retailer' ? null : 'retailer')}>
                                RETAILER <FaFilter style={{ fontSize: '8px', color: adFilters.retailer.length > 0 ? 'var(--color-savings)' : '#444' }} />
                                <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'merchantName' ? 1 : 0.3 }} onClick={(e) => { e.stopPropagation(); toggleSort('merchantName'); }} />
                              </div>
                              {showFilterDropdown === 'retailer' && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, background: '#000', border: '1px solid #333', zIndex: 100, minWidth: '200px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', padding: '10px' }}>
                                  <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="custom-scrollbar">
                                    {uniqueFilterOptions.retailers.map(r => (
                                      <div key={r} onClick={() => toggleFilter('retailer', r)} style={{ padding: '8px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: adFilters.retailer.includes(r) ? '#111' : 'transparent' }}>
                                        {adFilters.retailer.includes(r) ? <FaCheck style={{ color: 'var(--color-savings)' }} /> : <div style={{ width: '10px' }} />}
                                        {r.toUpperCase()}
                                      </div>
                                    ))}
                                  </div>
                                  <button onClick={() => setAdFilters(prev => ({ ...prev, retailer: [] }))} style={{ width: '100%', padding: '8px', background: 'transparent', border: 'none', borderTop: '1px solid #222', color: '#ff5252', fontSize: '9px', cursor: 'pointer', marginTop: '5px' }}>RESET_FILTER</button>
                                </div>
                              )}
                            </th>

                            {/* Gender Filter */}
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', position: 'relative' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowFilterDropdown(showFilterDropdown === 'gender' ? null : 'gender')}>
                                GENDER <FaFilter style={{ fontSize: '8px', color: adFilters.gender.length > 0 ? 'var(--color-savings)' : '#444' }} />
                              </div>
                              {showFilterDropdown === 'gender' && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, background: '#000', border: '1px solid #333', zIndex: 100, minWidth: '160px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', padding: '10px' }}>
                                  {uniqueFilterOptions.genders.map(g => (
                                    <div key={g} onClick={() => toggleFilter('gender', g)} style={{ padding: '8px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: adFilters.gender.includes(g) ? '#111' : 'transparent' }}>
                                      {adFilters.gender.includes(g) ? <FaCheck style={{ color: 'var(--color-savings)' }} /> : <div style={{ width: '10px' }} />}
                                      {g.toUpperCase()}
                                    </div>
                                  ))}
                                  <button onClick={() => setAdFilters(prev => ({ ...prev, gender: [] }))} style={{ width: '100%', padding: '8px', background: 'transparent', border: 'none', borderTop: '1px solid #222', color: '#ff5252', fontSize: '9px', cursor: 'pointer', marginTop: '5px' }}>RESET_FILTER</button>
                                </div>
                              )}
                            </th>

                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', cursor: 'pointer' }} onClick={() => toggleSort('originalPrice')}>
                              ORIGINAL_PRICE <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'originalPrice' ? 1 : 0.3 }} />
                            </th>
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', cursor: 'pointer' }} onClick={() => toggleSort('price')}>
                              SALE_PRICE <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'price' ? 1 : 0.3 }} />
                            </th>
                            <th style={{ padding: '15px 20px', fontSize: '9px', color: '#444', fontFamily: 'monospace', cursor: 'pointer', textAlign: 'right' }} onClick={() => toggleSort('calculatedDiscount')}>
                              DISCOUNT_% <FaSort style={{ fontSize: '8px', opacity: adSortConfig?.key === 'calculatedDiscount' ? 1 : 0.3 }} />
                            </th>
                          </tr>
                        </thead>
                        <tbody style={{ maxHeight: '600px', overflowY: 'auto' }}>
                          {filteredAndSortedProducts.map(p => {
                            const isSelected = selectedAdProducts.some(sp => sp._id === p._id);
                            return (
                              <tr 
                                key={p._id}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedAdProducts(prev => prev.filter(sp => sp._id !== p._id));
                                  } else {
                                    setSelectedAdProducts(prev => [...prev, p]);
                                  }
                                }}
                                style={{ 
                                  borderBottom: '1px solid #111', 
                                  cursor: 'pointer', 
                                  background: isSelected ? 'rgba(0, 230, 118, 0.03)' : 'transparent',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? 'rgba(0, 230, 118, 0.05)' : '#0d0d0d'}
                                onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? 'rgba(0, 230, 118, 0.03)' : 'transparent'}
                              >
                                <td style={{ padding: '12px 20px' }}>
                                  <div style={{ 
                                    width: '18px', 
                                    height: '18px', 
                                    border: `1px solid ${isSelected ? 'var(--color-savings)' : '#333'}`,
                                    background: isSelected ? 'var(--color-savings)' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}>
                                    {isSelected && <FaCheck style={{ color: '#000', fontSize: '10px' }} />}
                                  </div>
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  <img src={p.image || p.images?.[0]} style={{ width: '40px', height: '40px', objectFit: 'cover', border: '1px solid #222' }} alt="" />
                                </td>
                                <td 
                                  style={{ padding: '12px 20px', position: 'relative' }}
                                  onMouseEnter={() => setHoveredProductId(p._id)}
                                  onMouseLeave={() => setHoveredProductId(null)}
                                >
                                  <div style={{ fontSize: '11px', fontWeight: '900', color: '#fff' }}>{p.name}</div>
                                  <div style={{ fontSize: '8px', color: '#444', fontFamily: 'monospace', marginTop: '2px' }}>ID: {p._id.slice(-8).toUpperCase()}</div>
                                  
                                  {hoveredProductId === p._id && p.sizes && p.sizes.length > 0 && (
                                    <div style={{
                                      position: 'absolute',
                                      left: '20px',
                                      top: '100%',
                                      background: '#111',
                                      border: '1px solid var(--color-savings)',
                                      padding: '10px',
                                      zIndex: 1000,
                                      minWidth: '150px',
                                      boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
                                      pointerEvents: 'none'
                                    }}>
                                      <div style={{ fontSize: '8px', color: '#666', marginBottom: '5px', fontFamily: 'monospace' }}>AVAILABLE_SIZES</div>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                        {p.sizes.map((s, idx) => (
                                          <span key={idx} style={{ fontSize: '9px', background: '#222', color: 'var(--color-savings)', padding: '2px 6px', border: '1px solid #333' }}>{s}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  <span style={{ fontSize: '10px', color: '#888', fontWeight: '800' }}>{p.category?.toUpperCase()}</span>
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  <span style={{ fontSize: '10px', color: '#888', fontWeight: '800' }}>{p.brand?.toUpperCase()}</span>
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  <span style={{ fontSize: '10px', color: '#888', fontWeight: '800' }}>{p.merchantName?.toUpperCase()}</span>
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  {p.gender ? (
                                    <span style={{
                                      fontSize: '9px',
                                      fontWeight: '700',
                                      fontFamily: 'monospace',
                                      letterSpacing: '1.5px',
                                      textTransform: 'uppercase',
                                      padding: '3px 8px',
                                      border: `1px solid ${p.gender === 'women' ? '#e879a0' : p.gender === 'men' ? '#4da6ff' : p.gender === 'kids' ? '#fbbf24' : '#555'}`,
                                      color: p.gender === 'women' ? '#e879a0' : p.gender === 'men' ? '#4da6ff' : p.gender === 'kids' ? '#fbbf24' : '#666',
                                      borderRadius: '3px'
                                    }}>
                                      {p.gender}
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '9px', color: '#333', fontFamily: 'monospace' }}>—</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  <span style={{ fontSize: '11px', color: '#444', textDecoration: 'line-through' }}>${p.originalPrice || p.price}</span>
                                </td>
                                <td style={{ padding: '12px 20px' }}>
                                  <span style={{ fontSize: '12px', color: '#fff', fontWeight: '900' }}>${p.price}</span>
                                </td>
                                <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                                  <span style={{ 
                                    fontSize: '10px', 
                                    fontWeight: '900', 
                                    color: p.calculatedDiscount > 0 ? 'var(--color-savings)' : '#444',
                                    background: p.calculatedDiscount > 0 ? 'rgba(0, 230, 118, 0.1)' : 'transparent',
                                    padding: '4px 8px',
                                    fontFamily: 'monospace'
                                  }}>
                                    {p.calculatedDiscount > 0 ? `-${p.calculatedDiscount}%` : '--'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {filteredAndSortedProducts.length === 0 && (
                        <div style={{ padding: '100px', textAlign: 'center', color: '#444', fontSize: '12px', fontFamily: 'monospace' }}>
                           NO_DATA_MATCHES_CURRENT_PROTOCOL_FILTERS
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: '20px', padding: '10px 40px', background: '#000', borderTop: '1px solid #222' }}>
                      <Pagination 
                        currentPage={adPage} 
                        totalPages={adTotalPages} 
                        onPageChange={setAdPage} 
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* --- WORKFLOW: STEP 03 (MANIFEST) --- */}
            {adSubTab === 'manifest' && (
              <div className="animate-slide-up" style={{ display: 'flex', background: '#0a0a0a', minHeight: '900px' }}>
                {/* Configuration Sidebar */}
                <div style={{ width: '300px', padding: '40px', borderRight: '1px solid #111', display: 'flex', flexDirection: 'column', gap: '30px' }}>
                  <div>
                    <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#666', marginBottom: '20px' }}>DEPLOYMENT_SPECS</h3>
                    {selectedTemplate === 'brand_story' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button
                          type="button"
                          onClick={() => setAdViewMode('square')}
                          style={{ width: '100%', padding: '15px', background: '#fff', color: '#000', border: '1px solid #222', fontSize: '10px', fontWeight: '900', cursor: 'pointer' }}
                        >
                          <FaLayerGroup style={{ marginRight: '10px' }} /> FEED_POST (1:1) LOCKED
                        </button>
                        <div style={{ fontSize: '9px', color: '#666', fontFamily: 'monospace', lineHeight: 1.4 }}>
                          Brand Story exports as a 5-slide 1080×1080 Instagram carousel.
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button 
                          onClick={() => setAdViewMode('mobile')}
                          style={{ width: '100%', padding: '15px', background: adViewMode === 'mobile' ? '#fff' : '#000', color: adViewMode === 'mobile' ? '#000' : '#444', border: '1px solid #222', fontSize: '10px', fontWeight: '900', cursor: 'pointer' }}
                        >
                          <FaMobileAlt style={{ marginRight: '10px' }} /> REELS / STORIES (4:5)
                        </button>
                        <button 
                          onClick={() => setAdViewMode('square')}
                          style={{ width: '100%', padding: '15px', background: adViewMode === 'square' ? '#fff' : '#000', color: adViewMode === 'square' ? '#000' : '#444', border: '1px solid #222', fontSize: '10px', fontWeight: '900', cursor: 'pointer' }}
                        >
                          <FaLayerGroup style={{ marginRight: '10px' }} /> FEED_POST (1:1)
                        </button>
                        <button 
                          onClick={() => setAdViewMode('desktop')}
                          style={{ width: '100%', padding: '15px', background: adViewMode === 'desktop' ? '#fff' : '#000', color: adViewMode === 'desktop' ? '#000' : '#444', border: '1px solid #222', fontSize: '10px', fontWeight: '900', cursor: 'pointer' }}
                        >
                          <FaDesktop style={{ marginRight: '10px' }} /> CINEMATIC (21:9)
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{ background: '#050505', padding: '20px', border: '1px solid #111', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <div style={{ fontSize: '9px', color: '#444', marginBottom: '8px' }}>MANIFEST_SUMMARY</div>
                      <div style={{ fontSize: '10px', color: '#fff', marginBottom: '5px' }}>TEMPLATE: {selectedTemplate.toUpperCase()}</div>
                      {selectedTemplate === 'brand_story' ? (
                        <div style={{ fontSize: '10px', color: '#fff' }}>
                          SLIDE: {brandStorySlideIndex + 1} / {BRAND_STORY_SLIDE_COUNT} · {BRAND_STORY_SLIDES[brandStorySlideIndex]?.title}
                        </div>
                      ) : (
                        <div style={{ fontSize: '10px', color: '#fff' }}>ASSETS: {selectedAdProducts.length} TARGETS</div>
                      )}
                    </div>

                    {selectedTemplate === 'brand_story' ? (
                      <div style={{ borderTop: '1px solid #111', paddingTop: '15px' }}>
                        <div style={{ fontSize: '9px', color: '#444', marginBottom: '10px' }}>CAROUSEL_NAV</div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                          <button
                            type="button"
                            disabled={brandStorySlideIndex <= 0}
                            onClick={() => setBrandStorySlideIndex((i) => Math.max(0, i - 1))}
                            style={{ flex: 1, padding: '10px', background: '#000', border: '1px solid #222', color: '#fff', cursor: 'pointer' }}
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            type="button"
                            disabled={brandStorySlideIndex >= BRAND_STORY_SLIDE_COUNT - 1}
                            onClick={() => setBrandStorySlideIndex((i) => Math.min(BRAND_STORY_SLIDE_COUNT - 1, i + 1))}
                            style={{ flex: 1, padding: '10px', background: '#000', border: '1px solid #222', color: '#fff', cursor: 'pointer' }}
                          >
                            <FaChevronRight />
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {BRAND_STORY_SLIDES.map((slide, idx) => (
                            <button
                              key={slide.id}
                              type="button"
                              onClick={() => setBrandStorySlideIndex(idx)}
                              title={slide.label}
                              style={{
                                width: '28px',
                                height: '28px',
                                fontSize: '9px',
                                fontWeight: '900',
                                border: '1px solid',
                                borderColor: brandStorySlideIndex === idx ? '#00E676' : '#222',
                                background: brandStorySlideIndex === idx ? '#00E676' : '#000',
                                color: brandStorySlideIndex === idx ? '#000' : '#888',
                                cursor: 'pointer',
                              }}
                            >
                              {idx + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ borderTop: '1px solid #111', paddingTop: '15px' }}>
                        <div style={{ fontSize: '9px', color: '#444', marginBottom: '8px' }}>COLOUR_THEME_ENGINE</div>
                        <select 
                          className="retailers-search" 
                          style={{ width: '100%', padding: '10px', fontSize: '10px', background: '#000', color: '#fff', border: '1px solid #222' }}
                          value={adColorTheme}
                          onChange={(e) => setAdColorTheme(e.target.value)}
                        >
                          {selectedTemplate === 'product_showcase' ? (
                            Object.keys(SHOWCASE_THEMES).map(t => (
                              <option key={t} value={t}>{SHOWCASE_THEMES[t].name}</option>
                            ))
                          ) : (
                            Object.keys(COLOR_THEMES).map(t => (
                              <option key={t} value={t}>{COLOR_THEMES[t].name}</option>
                            ))
                          )}
                        </select>
                      </div>
                    )}
                  </div>

                  {selectedTemplate === 'brand_story' && (
                    <div style={{ background: '#050505', border: '1px solid #111', padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '9px', color: '#444' }}>CAPTION_PACK</div>
                        <button
                          type="button"
                          onClick={copyBrandStoryCaption}
                          style={{ background: 'none', border: '1px solid #333', color: captionCopied ? '#00E676' : '#fff', fontSize: '8px', fontWeight: '800', padding: '6px 8px', cursor: 'pointer' }}
                        >
                          <FaCopy style={{ marginRight: '4px' }} />
                          {captionCopied ? 'COPIED' : 'COPY'}
                        </button>
                      </div>
                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '9px', color: '#999', lineHeight: 1.45, margin: 0, fontFamily: 'monospace', maxHeight: '160px', overflow: 'auto' }}>
                        {BRAND_STORY_CAPTION}
                      </pre>
                    </div>
                  )}

                  <button 
                    className="brutalist-btn brutalist-btn-dark" 
                    onClick={handleGenerateManifest}
                    disabled={isDownloading || isRecording}
                    style={{ width: '100%', background: 'var(--color-savings)', color: '#000', padding: '20px', fontWeight: '900' }}
                  >
                    {isRecording
                      ? `RECORDING_${Math.round(recordingProgress)}%`
                      : isDownloading
                        ? <FaSyncAlt className="fa-spin" />
                        : (selectedTemplate === 'brand_story' ? 'DOWNLOAD_5_SLIDE_ZIP' : 'GENERATE_FINAL_MANIFEST')}
                  </button>
                </div>

                {/* Main Rendering Canvas */}
                <div style={{ flex: 1, padding: '60px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', background: '#000' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px' }}>
                    <div style={{ fontSize: '9px', color: '#444', fontFamily: 'monospace', letterSpacing: '2px' }}>
                      {selectedTemplate === 'brand_story'
                        ? `BRAND_STORY // SLIDE_${brandStorySlideIndex + 1}_OF_${BRAND_STORY_SLIDE_COUNT} // ${BRAND_STORY_SLIDES[brandStorySlideIndex]?.label}`
                        : `RENDER_READY // ${adViewMode.toUpperCase()} // 100%_FIDELITY`}
                    </div>
                    
                    <div style={{
                      width: adViewMode === 'mobile' ? '400px' : (adViewMode === 'square' ? '400px' : '800px'),
                      aspectRatio: adViewMode === 'mobile' ? '4/5' : (adViewMode === 'square' ? '1/1' : '16/9'),
                      overflow: 'hidden', background: '#000', position: 'relative', border: '1px solid #333', boxShadow: '0 50px 100px rgba(0,0,0,0.8)'
                    }}>
                      <div style={{ 
                        width: adViewMode === 'desktop' ? '1920px' : '1080px', 
                        height: adViewMode === 'mobile' ? '1350px' : '1080px', 
                        transform: `scale(${adViewMode === 'desktop' ? 800/1920 : 400/1080})`, 
                        transformOrigin: 'top left',
                        position: 'relative'
                      }}>
                        {(selectedTemplate === 'single_product_shoes' || selectedTemplate === 'single_product_clothing') && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              inset: 0, 
                              zIndex: 1000, 
                              cursor: isDragging ? 'grabbing' : 'grab',
                              pointerEvents: 'auto'
                            }} 
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                          />
                        )}
                        <iframe 
                          ref={previewRef}
                          title="Manifest Preview" 
                          srcDoc={adStyleCode} 
                          style={{ width: '100%', height: '100%', border: 'none' }} 
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '20px' }}>
                      <button className="brutalist-btn-outline" onClick={() => setAdSubTab('config')} style={{ padding: '10px 30px' }}>RECONFIGURE</button>
                      <button className="brutalist-btn-outline" onClick={() => setShowAdOverlay(true)} style={{ padding: '10px 30px' }}>FULL_SCREEN_INSPECT</button>
                      {(selectedTemplate === 'single_product_shoes' || selectedTemplate === 'single_product_clothing') && (
                        <button className="brutalist-btn-outline" onClick={() => setAdPosition({ x: 0, y: 0 })} style={{ padding: '10px 30px', color: '#ff5252', borderColor: '#ff5252' }}>RESET_POSITION</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );


      default: return null;
    }
  };

  return (
    <div className="profile-page-wrapper">
      <div className="profile-sidebar">
        <div className="profile-user-section">
          <div className="profile-user-circle" style={{ background: 'var(--color-savings)', color: '#000', fontWeight: '900' }}>RT</div>
          <div className="profile-user-name">SYSTEM_ADMIN</div>
          <div className="profile-user-email">PROTOCOL_V1.0.4_STABLE</div>
        </div>
        <nav className="profile-sidebar-nav">
          <ul>
            <li><button className={selectedTab === 'retailer-hub' ? 'active' : ''} onClick={() => setSelectedTab('retailer-hub')}><FaNetworkWired className="nav-icon" /> Retailer Hub</button></li>
            <li><button className={selectedTab === 'ingestion-engine' ? 'active' : ''} onClick={() => setSelectedTab('ingestion-engine')}><FaDatabase className="nav-icon" /> Ingestion Engine</button></li>
            <li><button className={selectedTab === 'analytics' ? 'active' : ''} onClick={() => setSelectedTab('analytics')}><FaChartLine className="nav-icon" /> Feed Analytics</button></li>
            <li><button className={selectedTab === 'advertising' ? 'active' : ''} onClick={() => setSelectedTab('advertising')}><FaAd className="nav-icon" /> Advertising</button></li>
            <li><button className={selectedTab === 'conflict' ? 'active' : ''} onClick={() => setSelectedTab('conflict')}><FaExchangeAlt className="nav-icon" /> Conflicts</button></li>
            <li><button className={selectedTab === 'banners' ? 'active' : ''} onClick={() => setSelectedTab('banners')}><FaImage className="nav-icon" /> Banners</button></li>
            <li style={{ marginTop: 'auto' }}><button className="logout-button" onClick={onLogout}><FaSignOutAlt className="nav-icon" /> Logout</button></li>
          </ul>
        </nav>
      </div>
      <div className="profile-main-content">
        <div className="technical-header-overlay">
          <h1 className="profile_heading">Admin Dashboard</h1>
          <div className="system-path">Settings // {selectedTab === 'retailer-hub' ? 'Retailer Hub' : selectedTab === 'ingestion-engine' ? 'Ingestion Engine' : selectedTab === 'analytics' ? 'Feed Analytics' : selectedTab === 'advertising' ? 'Advertising Lab' : selectedTab === 'conflict' ? 'Conflict Matrix' : selectedTab === 'banners' ? 'Banner Management' : 'Dashboard'}</div>
        </div>
        <div className="profile-info-container">{renderContent()}</div>
      </div>

      {/* --- SNIPPET POP-OUT OVERLAY --- */}
      {showAdOverlay && (
        <div className="snippet-popout-overlay" style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.98)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px'
        }}>
          {/* Close & Control Bar */}
          <div className="snippet-control-bar" style={{ 
            width: (adViewMode === 'mobile' ? 1080 : 1920) * adScale + 'px',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <div style={{ color: '#666', fontSize: '12px', fontFamily: 'monospace', display: 'flex', gap: '20px', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-savings)', fontWeight: '900' }}>SNIPPET_MODE // {adViewMode.toUpperCase()}</span>
              <button 
                onClick={handleDownloadImage}
                disabled={isDownloading}
                style={{ 
                  background: '#fff', 
                  border: 'none', 
                  color: '#000', 
                  padding: '10px 20px', 
                  fontWeight: '900', 
                  cursor: 'pointer', 
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 0 20px rgba(255,255,255,0.2)'
                }}
              >
                <FaDownload /> {isDownloading ? 'GENERATING...' : 'DOWNLOAD_PNG'}
              </button>
            </div>
            <button 
              onClick={() => setShowAdOverlay(false)}
              style={{ background: '#ff5252', border: 'none', color: '#000', padding: '10px 25px', fontWeight: '900', cursor: 'pointer', fontSize: '11px' }}
            >
              EXIT_SNIPPET_MODE [ESC]
            </button>
          </div>

          {/* Scaled Wrapper for Overlay */}
          <div className="snippet-record-canvas" style={{
            width: (adViewMode === 'mobile' ? 1080 : 1920) * adScale + 'px',
            height: (adViewMode === 'mobile' ? 1350 : 1080) * adScale + 'px',
            background: '#000',
            boxShadow: '0 0 120px rgba(0,230,118,0.25)',
            position: 'relative',
            border: '2px solid var(--color-savings)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div 
              className="ad-capture-wrapper"
              style={{ 
                width: adViewMode === 'mobile' ? '1080px' : '1920px',
                height: adViewMode === 'mobile' ? '1350px' : '1080px',
                transform: `scale(${adScale})`,
                transformOrigin: 'center center',
                position: 'absolute'
              }}
            >
              <iframe 
                ref={iframeRef}
                title="Snippet Preview"
                srcDoc={adStyleCode}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  border: 'none', 
                  overflow: 'hidden',
                  background: '#000',
                  pointerEvents: 'none'
                }}
              />
            </div>
          </div>
          
          <div className="snippet-capture-tip" style={{ marginTop: '20px', color: '#444', fontSize: '10px', fontFamily: 'monospace' }}>
            STATUS: READY_FOR_CAPTURE // RESOLUTION: {(adViewMode === 'mobile' ? '1080x1350' : '1920x1080')} // SCALE: {(adScale * 100).toFixed(0)}%
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRetailerDashboard;
